import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  TagIcon,
  CubeIcon,
  ShieldCheckIcon,
  EyeIcon,
  GiftIcon
} from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { supabase } from '../utils/supabase';
import { createProduct, productsApi, updateProduct } from '../utils/api';
import { FORMAT_PAR_DEFAUT, formatsPourModele } from '../constants/formatsImage';
// Import des constantes de catégories depuis le fichier centralisé
import {
  categories,
  subCategories,
  getCategoryIcon,
  getSuggestedTags
} from '../constants/productCategories';
import { getDeviseActive } from '../utils/devise';

// Helper pour générer des IDs uniques simples
let optionIdCounter = 0;
let valueIdCounter = 0;

// Fonction utilitaire pour générer les combinaisons (produit cartésien)
const cartesian = (...args) => {
  const r = [], max = args.length - 1;
  function helper(arr, i) {
    for (let j = 0, l = args[i].length; j < l; j++) {
      const a = arr.slice(0); // Clone arr
      a.push(args[i][j]);
      if (i === max)
        r.push(a);
      else
        helper(a, i + 1);
    }
  }
  if (args.length > 0) {
    helper([], 0);
  }
  return r;
};

const createDefaultAiSelection = () => ({
  name: true,
  description: true,
  sku: true,
  tags: true
});

const buildSelectionFromSuggestions = (suggestions = {}) => ({
  name: Boolean(suggestions?.name),
  description: Boolean(suggestions?.description),
  sku: Boolean(suggestions?.sku),
  tags: Array.isArray(suggestions?.tags) && suggestions.tags.length > 0
});

const mergeTagsWithoutDuplicates = (existing = [], incoming = []) => {
  const normalizedExisting = new Set(
    existing.map(tag => (tag || '').trim().toLowerCase()).filter(Boolean)
  );
  const merged = [...existing];

  (incoming || []).forEach((tag) => {
    const trimmed = (tag || '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (!normalizedExisting.has(key)) {
      normalizedExisting.add(key);
      merged.push(trimmed);
    }
  });

  return merged;
};

const AI_FIELD_OPTIONS = [
  { key: 'name', label: 'Nom du produit' },
  { key: 'description', label: 'Description' },
  { key: 'sku', label: 'Référence (SKU)' },
  { key: 'tags', label: 'Tags' }
];



// Composant de dialogue personnalisé pour l'insertion d'images
const ImageUrlDialog = ({ isOpen, onClose, onInsert }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [isValidUrl, setIsValidUrl] = useState(true);

  // Préréglages de taille communs
  const sizePresets = [
    { name: 'Petite (150x100)', width: '150', height: '100' },
    { name: 'Moyenne (300x200)', width: '300', height: '200' },
    { name: 'Grande (500x300)', width: '500', height: '300' },
    { name: 'Bannière (600x150)', width: '600', height: '150' },
    { name: 'Carrée (250x250)', width: '250', height: '250' }
  ];

  // Réinitialise la session de création à chaque ouverture pour empêcher les
  // références ou préférences d'un produit précédent d'être réutilisées par erreur.
  useEffect(() => {
    if (isOpen) {
      setImageUrl('');
      setAltText('');
      setWidth('300'); // Valeur par défaut
      setHeight('200'); // Valeur par défaut
      setMaintainAspectRatio(true);
      setIsValidUrl(true);
    }
  }, [isOpen]);

  const handleInsert = () => {
    if (!imageUrl.trim()) return;

    // Validation de l'URL
    const urlPattern = /^(https?:\/\/)|(data:image\/)/i;
    const imageExtPattern = /\.(jpeg|jpg|gif|png|webp|svg)(\?.*)?$/i;

    if (urlPattern.test(imageUrl) || imageExtPattern.test(imageUrl)) {
      // Validation des dimensions
      const widthNum = parseInt(width) || 300;
      const heightNum = parseInt(height) || 200;

      onInsert(imageUrl, altText || 'Image', {
        width: widthNum,
        height: heightNum,
        maintainAspectRatio
      });
      onClose();
    } else {
      setIsValidUrl(false);
    }
  };

  const handlePresetSelect = (preset) => {
    setWidth(preset.width);
    setHeight(preset.height);
  };

  const handleWidthChange = (newWidth) => {
    setWidth(newWidth);
    if (maintainAspectRatio && newWidth && height) {
      // Calculer la hauteur proportionnelle (ratio 3:2 par défaut)
      const ratio = 200 / 300; // ratio par défaut
      const newHeight = Math.round(parseInt(newWidth) * ratio);
      setHeight(newHeight.toString());
    }
  };

  const handleHeightChange = (newHeight) => {
    setHeight(newHeight);
    if (maintainAspectRatio && newHeight && width) {
      // Calculer la largeur proportionnelle
      const ratio = 300 / 200; // ratio par défaut
      const newWidth = Math.round(parseInt(newHeight) * ratio);
      setWidth(newWidth.toString());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInsert();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Insérer une image via URL
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="image-url" className="block text-sm font-medium text-gray-700 mb-2">
              URL de l'image *
            </label>
            <input
              id="image-url"
              type="url"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                setIsValidUrl(true);
              }}
              onKeyPress={handleKeyPress}
              placeholder="https://example.com/image.jpg"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${!isValidUrl ? 'border-red-300' : 'border-gray-300'
                }`}
              autoFocus
            />
            {!isValidUrl && (
              <p className="mt-1 text-sm text-red-600">
                Veuillez entrer une URL d'image valide
              </p>
            )}
          </div>

          <div>
            <label htmlFor="alt-text" className="block text-sm font-medium text-gray-700 mb-2">
              Texte alternatif (optionnel)
            </label>
            <input
              id="alt-text"
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Description de l'image"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Préréglages de taille */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tailles prédéfinies
            </label>
            <div className="grid grid-cols-2 gap-2">
              {sizePresets.map((preset, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handlePresetSelect(preset)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Dimensions personnalisées */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dimensions personnalisées (pixels)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="width" className="block text-xs text-gray-500 mb-1">
                  Largeur
                </label>
                <input
                  id="width"
                  type="number"
                  value={width}
                  onChange={(e) => handleWidthChange(e.target.value)}
                  placeholder="300"
                  min="50"
                  max="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="height" className="block text-xs text-gray-500 mb-1">
                  Hauteur
                </label>
                <input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) => handleHeightChange(e.target.value)}
                  placeholder="200"
                  min="50"
                  max="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Option pour maintenir les proportions */}
            <div className="mt-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={maintainAspectRatio}
                  onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Maintenir les proportions (3:2)
                </span>
              </label>
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-xs text-gray-600">
              <strong>Formats supportés :</strong> JPEG, JPG, PNG, GIF, WebP, SVG<br />
              <strong>Sources :</strong> URL HTTP/HTTPS ou images base64<br />
              <strong>Dimensions :</strong> 50px minimum, 1000px maximum par côté
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          <button
            onClick={handleInsert}
            disabled={!imageUrl.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Insérer l'image
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant de dialogue pour la génération d'images IA à partir d'un produit.
const NanoBananaModal = ({ isOpen, onClose, availableImages, productContext, onGenerate, isLoading }) => {
  // Identifiants regroupés ici pour maintenir les onglets synchronisés avec le modèle envoyé à NanoGPT.
  const GPT_IMAGE_SUNBURST = 'openai/gpt-image-2.5/sunburst/text-to-image';
  const NANO_BANANA_MODELS = [
    { value: 'nano-banana-2', label: 'NanoBanana 2' },
    { value: 'nano-banana-2-fast', label: 'NanoBanana 2 Fast' },
    { value: 'nano-banana-pro', label: 'NanoBanana Pro' }
  ];
  const [selectedImages, setSelectedImages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [creativeOptions, setCreativeOptions] = useState({ environment: 'studio', style: 'ecommerce', personPresence: 'none', gender: 'female', bodyType: 'standard', appearanceOrigin: 'african', productAction: 'alone' });
  const [model, setModel] = useState('nano-banana-2-fast');
  const [quality, setQuality] = useState('medium'); // Qualité uniquement pour GPT Image 2.5 Sunburst
  const [aspectRatio, setAspectRatio] = useState(FORMAT_PAR_DEFAUT);
  const [numberOfImages, setNumberOfImages] = useState(1);
  const [generateFrontBack, setGenerateFrontBack] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [referenceError, setReferenceError] = useState(false);
  // Contient le solde IA affiché dans la modale après lecture ou après débit réussi.
  const [aiBalance, setAiBalance] = useState(null);
  // Conserve une erreur de lecture non bloquante, le serveur vérifiant toujours le débit.
  const [aiBalanceError, setAiBalanceError] = useState(null);
  // Reçoit les tarifs depuis le serveur afin que l'estimation reproduise le débit réel.
  const [aiCosts, setAiCosts] = useState(null);
  // Mémorise les précisions personnelles chargées depuis le compte connecté.
  const [savedPromptPresets, setSavedPromptPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [isPresetFormOpen, setIsPresetFormOpen] = useState(false);
  const [isPresetsLoading, setIsPresetsLoading] = useState(false);
  const [isPresetSaving, setIsPresetSaving] = useState(false);
  const [deletingPresetId, setDeletingPresetId] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [presetFeedback, setPresetFeedback] = useState(null);

  // La clé de tarif reprend les modèles facturables reconnus par le serveur.
  const imageCount = generateFrontBack ? 2 : numberOfImages;
  const imageActionKey = model === 'nano-banana-2-fast' ? 'product_image_nano_banana_fast'
    : model === 'nano-banana-2' ? 'product_image_nano_banana_2'
      : model === 'nano-banana-pro' ? 'product_image_nano_banana_pro'
        : ['high', 'haute'].includes(String(quality || '').toLowerCase()) ? 'product_image_high'
          : ['medium', 'standard'].includes(String(quality || '').toLowerCase()) ? 'product_image_medium' : 'product_image_low';
  const pointsPerImage = Number(aiCosts?.[imageActionKey]) || 0;
  const generationCost = pointsPerImage * imageCount;
  const hasInsufficientAiBalance = aiBalance !== null && aiBalance < generationCost;
  const isAiPricingLoading = aiBalance === null || pointsPerImage === 0;

  /**
   * Bascule entre les familles GPT Image et NanoBanana sans perdre un modèle Nano
   * déjà choisi. L'onglet GPT sélectionne Sunburst, tandis que l'onglet NanoBanana
   * utilise NanoBanana 2 Fast par défaut lors de la première ouverture.
   *
   * @param {'gpt'|'nanobanana'} family - Famille de modèles demandée par l'utilisateur.
   */
  const selectModelFamily = (family) => {
    if (family === 'gpt') {
      setModel(GPT_IMAGE_SUNBURST);
      return;
    }
    if (!NANO_BANANA_MODELS.some(nanoModel => nanoModel.value === model)) {
      setModel('nano-banana-2-fast');
    }
  };

  const activeModelFamily = model === GPT_IMAGE_SUNBURST ? 'gpt' : 'nanobanana';

  useEffect(() => {
    if (isOpen) {
      setSelectedImages([]);
      setGeneratedImages([]);
      setGeneratedPrompt('');
      setPreviewImage(null);
      setReferenceError(false);
      setNumberOfImages(1);
      setGenerateFrontBack(false);
      setQuality('medium');
      setAspectRatio(FORMAT_PAR_DEFAUT);
      setPresetName('');
      setIsPresetFormOpen(false);
      setSelectedPresetId('');
      setPresetFeedback(null);
      // Les paramètres reviennent à une base neutre à chaque nouvelle génération.
      setCreativeOptions({ environment: 'studio', style: 'ecommerce', personPresence: 'none', gender: 'female', bodyType: 'standard', appearanceOrigin: 'african', productAction: 'alone' });
    }
  }, [isOpen]);

  /**
   * Charge les précisions réutilisables à chaque ouverture de la modale afin que
   * l'utilisateur retrouve ses derniers enregistrements sans recharger la page.
   * Le garde-fou isActive évite toute écriture d'état après la fermeture du dialogue.
   */
  useEffect(() => {
    let isActive = true;

    /**
     * Récupère les modèles personnels sans bloquer la génération si la lecture
     * échoue : la modale conserve alors toutes ses fonctions de génération.
     */
    const loadSavedPromptPresets = async () => {
      try {
        setIsPresetsLoading(true);
        const { data } = await productsApi.getAiImagePromptPresets();
        if (isActive) setSavedPromptPresets(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error('Impossible de charger les précisions enregistrées:', error);
        if (isActive) setPresetFeedback({ type: 'error', text: 'Les précisions enregistrées sont indisponibles pour le moment.' });
      } finally {
        if (isActive) setIsPresetsLoading(false);
      }
    };

    if (isOpen) loadSavedPromptPresets();
    return () => { isActive = false; };
  }, [isOpen]);

  /**
   * Charge le solde IA à chaque ouverture de la modale. Ce contrôle est seulement
   * informatif : le serveur reste la source d'autorité pour empêcher tout débit
   * au-delà du solde disponible, notamment si plusieurs onglets sont ouverts.
   */
  useEffect(() => {
    let isActive = true;

    /** Récupère le portefeuille sans afficher un ancien solde comme valeur certaine. */
    const loadAiWallet = async () => {
      try {
        setAiBalanceError(null);
        const { data } = await productsApi.getAiWallet();
        if (isActive && Number.isInteger(data?.balance)) setAiBalance(data.balance);
        if (isActive && data?.costs && typeof data.costs === 'object') setAiCosts(data.costs);
      } catch (error) {
        console.error('Impossible de charger le solde de crédits produit:', error);
        if (isActive) setAiBalanceError('Le solde IA est indisponible pour le moment.');
      }
    };

    if (isOpen) loadAiWallet();
    return () => { isActive = false; };
  }, [isOpen]);

  const toggleImageSelection = (img) => {
    if (selectedImages.find(i => i.id === img.id)) {
      setSelectedImages(prev => prev.filter(i => i.id !== img.id));
    } else {
      if (selectedImages.length >= 2) {
        // Max 2 images
        return;
      }
      setSelectedImages(prev => [...prev, img]);
      setReferenceError(false);
    }
  };

  /**
   * Lance la génération après validation de la référence obligatoire.
   * Les choix de mise en scène sont transmis sous forme structurée afin que Luna
   * puisse adapter le prompt au produit, sans demander à l'utilisateur d'en écrire un.
   */
  const handleGenerate = async () => {
    if (selectedImages.length === 0) {
      setReferenceError(true);
      return;
    }
    setGeneratedImages([]);
    onGenerate({
      prompt,
      images: selectedImages,
      productContext,
      creativeOptions,
      model,
      numberOfImages: generateFrontBack ? 2 : numberOfImages,
      generateFrontBack,
      quality,
      aspectRatio,
      onSuccess: (images, optimizedPrompt, remainingBalance) => {
        setGeneratedImages(images);
        setGeneratedPrompt(optimizedPrompt || prompt);
        if (Number.isInteger(remainingBalance)) setAiBalance(remainingBalance);
      }
    });
  };

  /**
   * Enregistre le texte actuellement écrit avec un nom court afin de le proposer
   * ultérieurement dans la liste des précisions personnelles réutilisables.
   */
  const handleSavePromptPreset = async () => {
    const name = presetName.trim();
    const instruction = prompt.trim();
    if (!name || !instruction) {
      setPresetFeedback({ type: 'error', text: 'Ajoutez un nom et une précision avant de l’enregistrer.' });
      return;
    }

    try {
      setIsPresetSaving(true);
      setPresetFeedback(null);
      const { data } = await productsApi.createAiImagePromptPreset({ name, instruction });
      if (!data?.data) throw new Error('Réponse de sauvegarde incomplète.');
      setSavedPromptPresets(previous => [data.data, ...previous]);
      setPresetName('');
      setIsPresetFormOpen(false);
      setPresetFeedback({ type: 'success', text: 'Précision enregistrée.' });
    } catch (error) {
      console.error('Impossible d’enregistrer la précision:', error);
      setPresetFeedback({ type: 'error', text: error.response?.data?.message || 'Impossible d’enregistrer cette précision.' });
    } finally {
      setIsPresetSaving(false);
    }
  };

  /**
   * Remplace le texte en cours par la précision sélectionnée. La réutilisation ne
   * lance pas la génération : l'utilisateur peut encore adapter le texte ensuite.
   *
   * @param {{instruction: string}} preset - Précision personnelle à réappliquer.
   */
  const handleApplyPromptPreset = (preset) => {
    setPrompt(preset.instruction);
    setPresetFeedback({ type: 'success', text: `« ${preset.name} » est appliquée.` });
  };

  /**
   * Applique la précision choisie dans le menu déroulant tout en mémorisant son
   * identifiant, nécessaire pour rendre la suppression explicite et sans ambiguïté.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} event - Valeur sélectionnée dans le menu.
   */
  const handleSelectPromptPreset = (event) => {
    const presetId = event.target.value;
    setSelectedPresetId(presetId);
    const preset = savedPromptPresets.find(item => item.id === presetId);
    if (preset) handleApplyPromptPreset(preset);
  };

  /**
   * Demande confirmation puis supprime une précision personnelle de la liste et
   * de la base, sans modifier le texte déjà présent dans le champ de précision.
   *
   * @param {{id: string, name: string}} preset - Précision personnelle à supprimer.
   */
  const handleDeletePromptPreset = async (preset) => {
    if (!window.confirm(`Supprimer la précision « ${preset.name} » ?`)) return;

    try {
      setDeletingPresetId(preset.id);
      setPresetFeedback(null);
      await productsApi.deleteAiImagePromptPreset(preset.id);
      setSavedPromptPresets(previous => previous.filter(item => item.id !== preset.id));
      setSelectedPresetId(previous => previous === preset.id ? '' : previous);
      setPresetFeedback({ type: 'success', text: 'Précision supprimée.' });
    } catch (error) {
      console.error('Impossible de supprimer la précision:', error);
      setPresetFeedback({ type: 'error', text: error.response?.data?.message || 'Impossible de supprimer cette précision.' });
    } finally {
      setDeletingPresetId(null);
    }
  };

  const handleSelectGeneratedImage = (imageUrl) => {
    // Si on veut permettre d'ajouter une image, on peut passer une fonction de callback "onSave"
    // Ici on suppose que le parent gère la sauvegarde
    onGenerate({
      prompt: generatedPrompt || prompt,
      images: selectedImages,
      model,
      saveImage: imageUrl
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={!isLoading ? onClose : undefined}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
                  Générer une image IA
                </h3>
                <div className="mt-4 space-y-4">

                  {/* Sélection des images de référence */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Images de référence <span className="text-red-600">*</span> (Max 2)
                    </label>
                    {availableImages.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Aucune image disponible. Ajoutez des images au produit pour les utiliser comme référence.</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 border rounded bg-gray-50">
                        {availableImages.map(img => (
                          <div
                            key={img.id}
                            onClick={() => toggleImageSelection(img)}
                            className={`relative cursor-pointer rounded border-2 overflow-hidden aspect-square ${selectedImages.find(i => i.id === img.id) ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
                              }`}
                          >
                            <img
                              src={img.previewUrl || img.existingUrl || img.image_url}
                              alt="Ref"
                              className="w-full h-full object-cover"
                            />
                            {selectedImages.find(i => i.id === img.id) && (
                              <div className="absolute top-0 right-0 bg-primary-600 text-white p-0.5 rounded-bl">
                                <ShieldCheckIcon className="w-3 h-3" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <p id="reference-images-help" className={`text-xs mt-1 ${referenceError ? 'text-red-700' : 'text-gray-500'}`} role={referenceError ? 'alert' : undefined}>
                      {referenceError ? 'Sélectionnez au moins une image de référence pour continuer.' : `Sélectionné : ${selectedImages.length}/2`}
                    </p>
                  </div>

                  {/* Les détails du personnage n'apparaissent que si l'utilisateur en demande un. */}
                  <fieldset className="bg-gray-50 p-3 rounded-md border border-gray-200 space-y-3">
                    <legend className="px-1 text-sm font-medium text-gray-700">Mise en scène</legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="block text-sm text-gray-700">Décor / environnement
                        <select value={creativeOptions.environment} onChange={(event) => setCreativeOptions(previous => ({ ...previous, environment: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"><option value="studio">Studio</option><option value="home">Maison</option><option value="street">Rue</option><option value="nature">Nature</option><option value="bathroom">Salle de bain</option><option value="restaurant">Restaurant</option><option value="beach">Plage</option></select>
                      </label>
                      <label className="block text-sm text-gray-700">Style
                        <select value={creativeOptions.style} onChange={(event) => setCreativeOptions(previous => ({ ...previous, style: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"><option value="ecommerce">E-commerce</option><option value="realistic">Photo réaliste</option><option value="premium_ad">Publicité premium</option></select>
                      </label>
                      <label className="block text-sm text-gray-700">Présence d’un personnage
                        <select value={creativeOptions.personPresence} onChange={(event) => setCreativeOptions(previous => ({ ...previous, personPresence: event.target.value, productAction: event.target.value === 'person' ? 'holding' : 'alone' }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"><option value="none">Aucun personnage</option><option value="person">Personne</option></select>
                      </label>
                      <label className="block text-sm text-gray-700">Action avec le produit
                        <select value={creativeOptions.productAction} disabled={creativeOptions.personPresence === 'none'} onChange={(event) => setCreativeOptions(previous => ({ ...previous, productAction: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"><option value="alone">Produit seul</option><option value="wearing">Porte le produit</option><option value="holding">Tient le produit</option><option value="using">Utilise le produit</option></select>
                      </label>
                    </div>
                    {creativeOptions.personPresence !== 'none' && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-gray-200 pt-3">
                        <label className="block text-sm text-gray-700">Sexe<select value={creativeOptions.gender} onChange={(event) => setCreativeOptions(previous => ({ ...previous, gender: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"><option value="female">Femme</option><option value="male">Homme</option><option value="neutral">Neutre</option></select></label>
                        <label className="block text-sm text-gray-700">Morphologie<select value={creativeOptions.bodyType} onChange={(event) => setCreativeOptions(previous => ({ ...previous, bodyType: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"><option value="slim">Taille fine</option><option value="standard">Standard</option><option value="round">Ronde</option><option value="hourglass_curvy">Hourglass / Curvy</option><option value="athletic">Athlétique</option><option value="plus_size">Grande taille</option></select></label>
                        <label className="block text-sm text-gray-700">Apparence<select value={creativeOptions.appearanceOrigin} onChange={(event) => setCreativeOptions(previous => ({ ...previous, appearanceOrigin: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"><option value="african">Africaine</option><option value="asian">Asiatique</option><option value="european">Européenne</option><option value="middle_eastern">Arabe / Moyen-Orient</option><option value="latina">Latine</option><option value="mixed">Métissée</option></select></label>
                      </div>
                    )}
                  </fieldset>

                  {/* Choix du modèle : famille, puis qualité GPT ou variante NanoBanana. */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                    <div className="flex gap-2 border-b border-gray-200" role="tablist" aria-label="Famille de modèles d'image">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeModelFamily === 'gpt'}
                        onClick={() => selectModelFamily('gpt')}
                        className={`border-b-2 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${activeModelFamily === 'gpt' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                      >
                        GPT Image
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeModelFamily === 'nanobanana'}
                        onClick={() => selectModelFamily('nanobanana')}
                        className={`border-b-2 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${activeModelFamily === 'nanobanana' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                      >
                        NanoBanana
                      </button>
                    </div>
                    <div className="mt-3" role="tablist" aria-label={activeModelFamily === 'gpt' ? 'Qualité GPT Image 2.5 Sunburst' : 'Variantes NanoBanana'}>
                      {activeModelFamily === 'gpt' ? (
                        <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' }
                        ].map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            role="tab"
                            aria-selected={quality === value}
                            onClick={() => setQuality(value)}
                            className={`px-2 py-2 text-xs font-medium rounded-md border ${quality === value
                              ? 'bg-blue-50 border-blue-500 text-blue-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {label}
                          </button>
                        ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {NANO_BANANA_MODELS.map((nanoModel) => (
                            <button
                              key={nanoModel.value}
                              type="button"
                              role="tab"
                              aria-selected={model === nanoModel.value}
                              onClick={() => setModel(nanoModel.value)}
                              className={`px-2 py-2 text-xs font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${model === nanoModel.value ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                              {nanoModel.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Le format est transmis à NanoGPT sous la clé aspect_ratio. */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Format de l’image</label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist" aria-label="Format de l'image générée">
                      {formatsPourModele(model).map((format) => (
                        <button
                          key={format.value}
                          type="button"
                          role="tab"
                          aria-selected={aspectRatio === format.value}
                          onClick={() => setAspectRatio(format.value)}
                          className={`px-2 py-2 text-xs font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${aspectRatio === format.value ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        >
                          {format.value}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Options de génération (Nombre d'images & Vue Avant/Arrière) */}
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Options de génération</label>

                    <div className="flex flex-col space-y-3">
                      {/* Checkbox Vue Avant/Arrière */}
                      <div className="flex items-center">
                        <input
                          id="front-back-option"
                          type="checkbox"
                          checked={generateFrontBack}
                          onChange={(e) => setGenerateFrontBack(e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="front-back-option" className="ml-2 block text-sm text-gray-900">
                          Générer vues Avant & Arrière (2 images)
                        </label>
                      </div>

                      {/* Sélecteur nombre d'images (désactivé si Front/Back est coché) */}
                      <div className={`transition-opacity ${generateFrontBack ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                        <label className="block text-xs text-gray-500 mb-1">
                          Nombre d'images à générer (Max 4)
                        </label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="range"
                            min="1"
                            max="4"
                            step="1"
                            value={numberOfImages}
                            onChange={(e) => setNumberOfImages(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <span className="text-sm font-bold text-gray-700 w-8 text-center border rounded bg-white">
                            {numberOfImages}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Présente solde et coût dans une carte unique, lisible avant l'action irréversible. */}
                  <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm" aria-label="Résumé des crédits de génération">
                    <div className="grid grid-cols-2 divide-x divide-gray-200">
                      <div className="px-4 py-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Solde</p>
                        {aiBalance === null ? (
                          <p className="mt-1 text-sm font-medium text-gray-600">Chargement…</p>
                        ) : (
                          <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{aiBalance} <span className="text-sm font-medium text-gray-600">crédits</span></p>
                        )}
                      </div>
                      <div className="bg-primary-50 px-4 py-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Cette génération</p>
                        {isAiPricingLoading ? (
                          <p className="mt-1 text-sm font-medium text-primary-800">Calcul du coût…</p>
                        ) : (
                          <>
                            <p className="mt-1 text-lg font-semibold tabular-nums text-primary-900">{generationCost} <span className="text-sm font-medium text-primary-700">crédits</span></p>
                            <p className="mt-0.5 text-xs tabular-nums text-primary-700">{pointsPerImage} / image · {imageCount} image{imageCount > 1 ? 's' : ''}</p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`px-4 py-2 text-sm ${hasInsufficientAiBalance ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`} role="status" aria-live="polite" aria-atomic="true">
                      {hasInsufficientAiBalance ? (
                        <>Crédits insuffisants : il manque {generationCost - aiBalance} crédit{generationCost - aiBalance > 1 ? 's' : ''}.</>
                      ) : isAiPricingLoading ? (
                        <>Vérification des crédits en cours…</>
                      ) : (
                        <>Solde suffisant pour cette génération.</>
                      )}
                      {aiBalanceError && <span className="block mt-1 text-amber-800">{aiBalanceError}</span>}
                    </div>
                  </section>

                  {/* Prompt */}
                  <div>
                    <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1">
                      Précision facultative
                    </label>
                    <textarea
                      id="prompt"
                      rows={4}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      maxLength={500}
                      placeholder="Ex. : lumière douce, sans texte sur l’image"
                    />
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-gray-500">{prompt.length}/500 caractères</p>
                      <button
                        type="button"
                        disabled={!prompt.trim() || isPresetSaving}
                        onClick={() => {
                          setPresetFeedback(null);
                          setIsPresetFormOpen(true);
                        }}
                        className="inline-flex items-center rounded-md border border-primary-300 bg-white px-3 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Enregistrer cette précision
                      </button>
                    </div>

                    {isPresetFormOpen && (
                      <div className="mt-3 rounded-md border border-primary-200 bg-primary-50 p-3">
                        <label htmlFor="prompt-preset-name" className="block text-sm font-medium text-gray-800">
                          Nom de la précision
                        </label>
                        <input
                          id="prompt-preset-name"
                          type="text"
                          value={presetName}
                          onChange={(event) => setPresetName(event.target.value)}
                          maxLength={80}
                          placeholder="Ex. Marche avec sac"
                          className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={isPresetSaving}
                            onClick={() => {
                              setPresetName('');
                              setIsPresetFormOpen(false);
                            }}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            disabled={!presetName.trim() || isPresetSaving}
                            onClick={handleSavePromptPreset}
                            className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isPresetSaving ? 'Enregistrement…' : 'Enregistrer'}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 border-t border-gray-200 pt-3">
                      <label htmlFor="saved-prompt-presets" className="block text-sm font-medium text-gray-800">
                        Mes précisions enregistrées
                      </label>
                      <select
                        id="saved-prompt-presets"
                        value={selectedPresetId}
                        disabled={isPresetsLoading || savedPromptPresets.length === 0}
                        onChange={handleSelectPromptPreset}
                        className="mt-2 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                      >
                        <option value="">
                          {isPresetsLoading ? 'Chargement des précisions…' : savedPromptPresets.length === 0 ? 'Aucune précision enregistrée' : 'Choisir une précision'}
                        </option>
                        {savedPromptPresets.map((preset) => (
                          <option key={preset.id} value={preset.id}>{preset.name}</option>
                        ))}
                      </select>
                      {savedPromptPresets.length === 0 && !isPresetsLoading && (
                        <p className="mt-2 text-sm text-gray-500">Enregistrez une précision pour la réutiliser ici.</p>
                      )}
                      {selectedPresetId && (
                        <button
                          type="button"
                          disabled={deletingPresetId === selectedPresetId}
                          onClick={() => {
                            const preset = savedPromptPresets.find(item => item.id === selectedPresetId);
                            if (preset) handleDeletePromptPreset(preset);
                          }}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                          {deletingPresetId === selectedPresetId ? 'Suppression…' : 'Supprimer la précision sélectionnée'}
                        </button>
                      )}
                    </div>

                    {presetFeedback && (
                      <p className={`mt-3 text-sm ${presetFeedback.type === 'error' ? 'text-red-700' : 'text-emerald-700'}`} role="status" aria-live="polite">
                        {presetFeedback.text}
                      </p>
                    )}
                  </div>

                  {/* Résultats générés */}
                  {generatedImages.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Résultats générés (Cliquez pour agrandir)</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {generatedImages.map((url, idx) => (
                          <div
                            key={idx}
                            onClick={() => setPreviewImage(url)}
                            className="relative group cursor-pointer aspect-square rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-primary-500 transition-all"
                          >
                            <img src={url} alt={`Généré ${idx}`} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                              <ArrowUpTrayIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              disabled={isLoading || selectedImages.length === 0 || hasInsufficientAiBalance || isAiPricingLoading}
              onClick={handleGenerate}
              aria-describedby="reference-images-help"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Générer
                </>
              ) : (
                isAiPricingLoading ? 'Calcul du coût…' : `Générer · ${generationCost} crédits`
              )}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>

      {/* Aperçu Lightbox */}
      {previewImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh] p-4 flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img src={previewImage} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" alt="Aperçu" />
            <div className="mt-6 flex justify-center gap-4">
              <button
                onClick={() => setPreviewImage(null)}
                className="px-6 py-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
              <button
                onClick={() => { handleSelectGeneratedImage(previewImage); setPreviewImage(null); }}
                className="px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-500 transition-colors font-medium flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Ajouter au produit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductFormPageComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Pour l'édition
  const isEditMode = !!id;

  // Récupérer le contexte de navigation depuis lequel on vient (s'il existe)
  const returnPage = location.state?.returnPage || 1;
  const productIds = location.state?.productIds || [];
  const currentIndex = location.state?.currentIndex ?? -1;
  const navigationContext = {
    returnPage,
    productIds,
    currentIndex,
    searchTerm: location.state?.searchTerm || '',
    selectedCategory: location.state?.selectedCategory || '',
    selectedStatus: location.state?.selectedStatus || ''
  };

  // Vérifier si on peut naviguer vers le produit précédent/suivant
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < productIds.length - 1;

  const [productOptions, setProductOptions] = useState([]); // ex: [{ id: o1, name: 'Couleur', values: [{id: v1, name: 'Rouge'}, {id: v2, name: 'Bleu'}] }]

  // Suggestions d'options normalisées chargées depuis la base
  const [optionDefinitionSuggestions, setOptionDefinitionSuggestions] = useState([]); // [{id, name, slug}]
  const [optionValueSuggestions, setOptionValueSuggestions] = useState({}); // { optionId: [{id, name, slug, display_hex}] }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditMode);

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    subCategory: '',
    price: '',
    discountPrice: '',
    status: 'active',
    stockLevel: '',
    description: '',
    images: [],
    barcode: '',
    has_variants: false,
    options: [],
    variations: [],
    tags: [] // Array de strings pour les tags
  });

  const [errors, setErrors] = useState({});

  const [imageSelectionModal, setImageSelectionModal] = useState({
    isOpen: false,
    variant: null,
    mode: 'variant'
  });

  // État pour la boîte de dialogue d'insertion d'images via URL
  const [imageUrlDialog, setImageUrlDialog] = useState({
    isOpen: false,
    quillRef: null
  });

  const aiMenuRef = useRef(null);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [aiProviders, setAiProviders] = useState({
    chatgpt: { available: false },
    claude: { available: false }
  });
  const [aiLoadingProvider, setAiLoadingProvider] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiApplySelection, setAiApplySelection] = useState(createDefaultAiSelection);

  // État pour NanoBanana
  const [isNanoBananaOpen, setIsNanoBananaOpen] = useState(false);
  const [nanoBananaLoading, setNanoBananaLoading] = useState(false);

  // État pour les images générées par IA (depuis la base de données)
  const [savedGeneratedImages, setSavedGeneratedImages] = useState([]);
  // Identifie l'image en cours de suppression pour empêcher les doubles clics.
  const [generatedImageDeletionId, setGeneratedImageDeletionId] = useState(null);
  // Affiche la confirmation après retrait d'une image de la bibliothèque.
  const [generatedImagesFeedback, setGeneratedImagesFeedback] = useState(null);

  // État pour la prévisualisation des images générées
  const [previewGeneratedImage, setPreviewGeneratedImage] = useState(null);

  // État de l'image produit affichée en grand depuis l'icône œil de la galerie.
  const [previewProductImage, setPreviewProductImage] = useState(null);

  // État de chargement pour l'upload manuel dans la galerie IA
  const [manualUploadLoading, setManualUploadLoading] = useState(false);
  // Ref sur l'input file caché pour l'upload manuel
  const manualUploadInputRef = useRef(null);

  // État pour le Drag & Drop des images
  const [draggedImageId, setDraggedImageId] = useState(null);

  const isMounted = useRef(false);
  const quillRef = useRef(null);
  const hasSuggestionValue = useCallback((fieldKey) => {
    if (!aiSuggestions) return false;
    if (fieldKey === 'tags') {
      return Array.isArray(aiSuggestions.tags) && aiSuggestions.tags.length > 0;
    }
    return Boolean(aiSuggestions[fieldKey]);
  }, [aiSuggestions]);

  const handleToggleAiField = useCallback((fieldKey) => {
    if (!hasSuggestionValue(fieldKey)) return;
    setAiApplySelection(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  }, [hasSuggestionValue]);

  const handleClearAiSuggestions = useCallback(() => {
    setAiSuggestions(null);
    setAiApplySelection(createDefaultAiSelection());
  }, []);

  // --- Gestion des Options ---
  const addOptionType = () => {
    optionIdCounter++;
    setProductOptions(prev => [
      ...prev,
      { id: `o${optionIdCounter}`, name: '', values: [] }
    ]);
  };

  const removeOptionType = (optionId) => {
    setProductOptions(prev => prev.filter(opt => opt.id !== optionId));
  };

  // Capitalise la première lettre et met le reste en minuscules (ex: "COULEUR" → "Couleur")
  const capitalizeFirst = (str) => {
    const trimmed = str.trim();
    if (!trimmed) return trimmed;
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  };

  const handleOptionNameChange = (optionId, newName) => {
    // Normaliser le nom d'option en Capitalize pour éviter les doublons de casse
    const normalizedName = capitalizeFirst(newName);
    setProductOptions(prev =>
      prev.map(opt => opt.id === optionId ? { ...opt, name: normalizedName } : opt)
    );

    // Charger les valeurs existantes depuis la base pour cette option (autocomplete)
    const slug = newName.trim().toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    if (slug) {
      productsApi.getOptionValues(slug)
        .then(({ data }) => {
          setOptionValueSuggestions(prev => ({ ...prev, [optionId]: data || [] }));
        })
        .catch(() => { }); // silencieux si pas de valeurs existantes
    } else {
      // Réinitialiser les suggestions si le nom est vide
      setOptionValueSuggestions(prev => ({ ...prev, [optionId]: [] }));
    }
  };

  const addOptionValue = (optionId, newValueName) => {
    if (!newValueName.trim()) return; // Ne pas ajouter de valeur vide

    // Normaliser la valeur en Capitalize (ex: "ROUGE" → "Rouge")
    const normalizedValue = capitalizeFirst(newValueName);

    valueIdCounter++;
    setProductOptions(prev =>
      prev.map(opt => {
        if (opt.id === optionId) {
          // Vérifier si la valeur existe déjà (insensible à la casse, après normalisation)
          const exists = opt.values.some(val => val.name.toLowerCase() === normalizedValue.toLowerCase());
          if (!exists) {
            return {
              ...opt,
              values: [...opt.values, { id: `v${valueIdCounter}`, name: normalizedValue }]
            };
          }
        }
        return opt;
      })
    );
  };

  const removeOptionValue = (optionId, valueId) => {
    setProductOptions(prev =>
      prev.map(opt =>
        opt.id === optionId ? { ...opt, values: opt.values.filter(val => val.id !== valueId) } : opt
      )
    );
  };

  // Composant interne pour gérer l'ajout de valeurs d'option
  const OptionValueInput = ({ optionId }) => {
    const [valueName, setValueName] = useState('');

    const handleAdd = (nameToAdd) => {
      const name = nameToAdd ?? valueName;
      addOptionValue(optionId, name);
      setValueName('');
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    };

    // Suggestions de valeurs pour cette option (depuis les tables normalisées)
    const suggestions = (optionValueSuggestions[optionId] || []).filter(
      sug => !productOptions
        .find(opt => opt.id === optionId)?.values
        .some(v => v.name.toLowerCase() === sug.name.toLowerCase())
    );

    return (
      <div className="mt-1">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={valueName}
            onChange={(e) => setValueName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nouvelle valeur (ex: Rouge)"
            className="flex-grow shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
          />
          <button
            type="button"
            onClick={() => handleAdd()}
            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Suggestions de valeurs existantes depuis la base */}
        {suggestions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {suggestions.map(sug => (
              <button
                key={sug.id}
                type="button"
                onClick={() => handleAdd(sug.name)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-gray-300 bg-gray-50 hover:bg-primary-50 hover:border-primary-400 text-gray-600 hover:text-primary-700 transition-colors"
                title={`Ajouter "${sug.name}"`}
              >
                {/* Pastille de couleur si display_hex disponible */}
                {sug.display_hex && (
                  <span
                    className="inline-block w-3 h-3 rounded-full border border-gray-300"
                    style={{ backgroundColor: sug.display_hex }}
                  />
                )}
                {sug.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };
  // --- Fin Gestion des Options ---

  // --- Génération et Gestion des Variations --- 
  const generateVariations = useCallback(() => {

    if (!formData.has_variants || productOptions.length === 0 || productOptions.some(opt => !opt.name.trim() || opt.values.length === 0)) {
      setFormData(prev => {
        if (prev.variations.length > 0) return { ...prev, variations: [] };
        return prev;
      });
      return;
    }

    const optionValuesArrays = productOptions.map(opt => opt.values);
    const combinations = cartesian(...optionValuesArrays);

    setFormData(prevFormData => {
      const currentVariations = prevFormData.variations;
      const currentBaseSku = prevFormData.sku || ''; // Lire aussi le SKU de base précédent
      console.log('Current Variations:', currentVariations);
      const newVariations = combinations.map((combo, index) => {
        const optionsObject = {};
        const titleParts = [];
        const valueIds = [];
        const skuParts = [currentBaseSku]; // Commencer le SKU avec le SKU de base

        productOptions.forEach((opt, i) => {
          optionsObject[opt.name] = combo[i].name;
          titleParts.push(combo[i].name);
          valueIds.push(combo[i].id);
          // Générer la partie du SKU pour cette valeur d'option
          const valueName = combo[i].name;
          const skuPart = valueName.substring(0, 2).toUpperCase();
          skuParts.push(skuPart);
        });

        const generatedSku = skuParts.filter(Boolean).join('-'); // Joindre les parties avec un tiret

        // Fonction pour comparer deux objets d'options de manière indépendante de l'ordre
        const areOptionsEqual = (options1, options2) => {
          const keys1 = Object.keys(options1 || {});
          const keys2 = Object.keys(options2 || {});

          if (keys1.length !== keys2.length) return false;

          return keys1.every(key => options1[key] === options2[key]);
        };

        const existingVariation = currentVariations.find(v =>
          areOptionsEqual(v.options, optionsObject) || v.sku === generatedSku
        );

        const tempId = `temp_${valueIds.sort().join('_')}`;

        return {
          id: existingVariation?.id || tempId,
          options: optionsObject,
          title: titleParts.join(' / '),
          price: existingVariation?.price || '',
          // Utiliser le SKU généré
          sku: generatedSku,
          stock: existingVariation?.stock || '0',
          compare_at_price: existingVariation?.compare_at_price || '',
          barcode: existingVariation?.barcode || ''
        };
      });

      if (JSON.stringify(currentVariations) !== JSON.stringify(newVariations)) {
        return { ...prevFormData, variations: newVariations };
      }
      return prevFormData;
    });

    // Dépendre aussi de formData.sku pour regénérer si le SKU de base change
  }, [productOptions, formData.has_variants, formData.sku]);

  // useEffect POUR LA REGENERATION des variations (Modifié)
  useEffect(() => {
    // Ne pas exécuter la logique de régénération pendant/avant la fin du chargement initial
    if (!isMounted.current) {
      console.log("useEffect [options/variants/sku]: Skipping initial mount/load phase");
      return;
    }

    console.log("useEffect [options/variants/sku]: Running generation/clearing logic...", formData);
    if (formData.has_variants) {
      generateVariations();
    } else {
      // Si on désactive les variations, s'assurer que le tableau est vide
      if (formData.variations.length > 0) {
        console.log("Clearing variations because has_variants is false.");
        setFormData(prev => ({ ...prev, variations: [] }));
      }
    }
    // Dépendances qui déclenchent la RE-génération
    // generateVariations est inclus car sa définition dépend de certaines de ces dépendances via useCallback
  }, [productOptions, formData.has_variants, formData.sku, generateVariations]);

  const handleVariationDetailChange = (variationIndex, field, value) => {
    setFormData(prev => {
      const updatedVariations = [...prev.variations];
      updatedVariations[variationIndex] = {
        ...updatedVariations[variationIndex],
        [field]: value
      };
      return { ...prev, variations: updatedVariations };
    });
    if (errors[`variation_${variationIndex}_${field}`]) {
      setErrors(prev => ({ ...prev, [`variation_${variationIndex}_${field}`]: null }));
    }
  };
  // --- Fin Gestion des Variations ---

  // Effet pour ajouter l'icône du bouton d'image personnalisé et styles d'images
  useEffect(() => {
    // Ajouter le style CSS pour le bouton d'image personnalisé et l'affichage des images
    const style = document.createElement('style');
    style.textContent = `
      .ql-image-url:before {
        content: '🖼️';
        font-size: 14px;
      }
      .ql-image-url {
        position: relative;
      }
      .ql-toolbar .ql-image-url {
        width: 28px;
        height: 24px;
      }
      
      /* Styles pour les images dans l'éditeur */
      .ql-editor img {
        display: block;
        margin: 10px auto;
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        border: 1px solid #e5e7eb;
      }
      
      /* Images avec dimensions personnalisées gardent leurs tailles */
      .ql-editor img[style*="width"] {
        max-width: none !important;
      }
      
      .ql-editor img[style*="height"] {
        max-height: none !important;
      }
      
      /* Images sans dimensions personnalisées utilisent les limites par défaut */
      .ql-editor img:not([style*="width"]):not([style*="height"]) {
        max-width: 100%;
        height: auto;
      }
      
      /* Style de hover pour les images */
      .ql-editor img:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        transform: scale(1.02);
        transition: all 0.2s ease-in-out;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // --- Gestion Images Multiples ---
  let imageIdCounter = 0;

  const handleFileSelect = (file, callback) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      callback({ file: file, previewUrl: reader.result });
    };
    reader.onerror = () => {
      setSubmitError("Erreur lors de la lecture du fichier image.");
      console.error("FileReader error");
    };
    reader.readAsDataURL(file);
  };

  const addMultipleProductImages = (files) => {
    const currentProductImagesCount = formData.images.filter(img => !img.option_values || Object.keys(img.option_values).length === 0).length;

    Array.from(files).forEach((file, index) => {
      handleFileSelect(file, (imageData) => {
        imageIdCounter++;
        const newImage = {
          id: `new_${imageIdCounter}`,
          file: imageData.file,
          previewUrl: imageData.previewUrl,
          alt_text: `Image du produit ${currentProductImagesCount + index + 1}`,
          position: formData.images.length + index + 1,
          is_main: currentProductImagesCount === 0 && index === 0,
          option_values: null
        };

        setFormData(prev => ({
          ...prev,
          images: [...prev.images, newImage]
        }));
      });
    });

    if (submitError) setSubmitError(null);
  };

  const removeImage = (imageId) => {
    setFormData(prev => {
      const updatedImages = prev.images.filter(img => img.id !== imageId);
      const reorderedImages = updatedImages.map((img, index) => ({
        ...img,
        position: index + 1
      }));

      const hasMainImage = reorderedImages.some(img => img.is_main && (!img.option_values || Object.keys(img.option_values).length === 0));
      if (!hasMainImage) {
        const firstGeneralImage = reorderedImages.find(img => !img.option_values || Object.keys(img.option_values).length === 0);
        if (firstGeneralImage) {
          firstGeneralImage.is_main = true;
        }
      }

      return { ...prev, images: reorderedImages };
    });
  };

  const setMainImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => ({
        ...img,
        is_main: img.id === imageId && (!img.option_values || Object.keys(img.option_values).length === 0)
      }))
    }));
  };

  // Fonction pour déplacer une image (changer sa position)
  const moveImage = (imageId, direction) => {
    setFormData(prev => {
      // 1. On travaille sur toutes les images triées par position
      const images = [...prev.images];

      // On travaille sur la liste triée pour identifier les voisins visuels
      const sortedImages = images.sort((a, b) => a.position - b.position);

      const currentIndex = sortedImages.findIndex(img => img.id === imageId);
      if (currentIndex === -1) return prev;

      // Vérifications des limites
      if (direction === 'left' && currentIndex === 0) return prev;
      if (direction === 'right' && currentIndex === sortedImages.length - 1) return prev;

      const targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;

      // 2. Échanger les positions
      // On échange simplement les valeurs de 'position' entre les deux images
      const currentPos = sortedImages[currentIndex].position;
      const targetPos = sortedImages[targetIndex].position;

      sortedImages[currentIndex].position = targetPos;
      sortedImages[targetIndex].position = currentPos;

      // 3. Gestion de l'image principale (is_main)
      // Si on déplace une image en position 1, elle devrait logiquement devenir la principale
      const newSortedImages = sortedImages.sort((a, b) => a.position - b.position);

      const updatedImages = newSortedImages.map((img, index) => {
        // La première image (index 0) devient main, les autres perdent main
        // Uniquement pour les images générales (sans option_values)
        if (!img.option_values || Object.keys(img.option_values).length === 0) {
          return { ...img, is_main: index === 0 };
        }
        return img;
      });

      return { ...prev, images: updatedImages };
    });
  };

  const handleDragStart = (e, imageId) => {
    setDraggedImageId(imageId);
    e.dataTransfer.effectAllowed = 'move';
    // Rendre l'élément un peu transparent lors du drag pourrait être géré via CSS avec une classe 'dragging'
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Nécessaire pour permettre le drop
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetImageId) => {
    e.preventDefault();

    if (!draggedImageId || draggedImageId === targetImageId) {
      setDraggedImageId(null);
      return;
    }

    setFormData(prev => {
      const images = [...prev.images];
      const sortedImages = images.sort((a, b) => a.position - b.position);

      const dragIndex = sortedImages.findIndex(img => img.id === draggedImageId);
      const dropIndex = sortedImages.findIndex(img => img.id === targetImageId);

      if (dragIndex === -1 || dropIndex === -1) return prev;

      // Retirer l'image déplacée
      const [draggedImage] = sortedImages.splice(dragIndex, 1);
      // L'insérer à la nouvelle position
      sortedImages.splice(dropIndex, 0, draggedImage);

      // Recalculer les positions pour tout le monde
      const updatedImages = sortedImages.map((img, index) => ({
        ...img,
        position: index + 1,
        is_main: index === 0 && (!img.option_values || Object.keys(img.option_values).length === 0)
      }));

      return { ...prev, images: updatedImages };
    });

    setDraggedImageId(null);
  };

  const getProductImages = () => {
    // Retourner TOUTES les images triées par position, incluant celles des variantes
    return formData.images.sort((a, b) => a.position - b.position);
  };

  /**
   * Ouvre une image produit en grand sans modifier sa position, son statut principal
   * ni ses associations de variantes. Le chemin d'image est résolu une seule fois
   * afin que la modale fonctionne autant pour une image locale que déjà enregistrée.
   */
  const openProductImagePreview = (image) => {
    setPreviewProductImage({
      src: image.previewUrl || image.existingUrl || image.image_url,
      alt: image.alt_text || 'Aperçu de l’image du produit'
    });
  };

  // Obtenir les images d'un variant spécifique (basé sur ses options)
  const getVariantImages = (variantId) => {
    const variant = formData.variations.find(v => v.id === variantId);
    if (!variant || !variant.options) return [];

    return formData.images.filter(img => {
      if (!img.option_values) return false;

      // Vérifier si l'image correspond à au moins une valeur d'option du variant
      return Object.entries(img.option_values).some(([optionName, optionValue]) => {
        return variant.options[optionName] === optionValue;
      });
    }).sort((a, b) => a.position - b.position);
  };

  // Obtenir les images pour une valeur d'option spécifique
  const getImagesForOptionValue = (optionName, optionValue) => {
    return formData.images.filter(img =>
      img.option_values && img.option_values[optionName] === optionValue
    ).sort((a, b) => a.position - b.position);
  };

  // Ajouter une image générée au produit
  const addGeneratedImageToProduct = (genImage) => {
    imageIdCounter++;
    const newImage = {
      id: `gen_${genImage.id}_${imageIdCounter}`,
      existingUrl: genImage.image_url,
      image_url: genImage.image_url,
      alt_text: genImage.prompt ? `Image générée: ${genImage.prompt.substring(0, 50)}...` : 'Image générée par IA',
      position: formData.images.length + 1,
      is_main: formData.images.length === 0, // Définir comme principale si c'est la première image
      option_values: null
    };

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, newImage]
    }));
  };

  /**
   * Demande confirmation, puis retire une image de la bibliothèque générée.
   * Le fichier est conservé lorsque l'image reste présente dans le formulaire
   * courant ; cela évite de casser une image qui n'est pas encore enregistrée.
   *
   * @param {Object} genImage - Enregistrement généré à supprimer.
   */
  const handleDeleteGeneratedImage = async (genImage) => {
    const isUsedInCurrentForm = formData.images.some((image) =>
      (image.existingUrl || image.image_url || image.previewUrl) === genImage.image_url
    );
    const confirmationMessage = isUsedInCurrentForm
      ? 'Cette image est déjà utilisée dans le formulaire en cours. La supprimer de la bibliothèque ? Elle restera visible dans le produit tant que vous ne la retirez pas de la galerie produit.'
      : 'Supprimer cette image de la bibliothèque générée ? Cette action est définitive.';

    if (!window.confirm(confirmationMessage)) return;

    setGeneratedImageDeletionId(genImage.id);
    setGeneratedImagesFeedback(null);
    try {
      await productsApi.deleteGeneratedImage(genImage.id, isUsedInCurrentForm);
      setSavedGeneratedImages((previousImages) => previousImages.filter((image) => image.id !== genImage.id));
      setPreviewGeneratedImage((previousImage) => previousImage?.id === genImage.id ? null : previousImage);
      setGeneratedImagesFeedback('Image supprimée de la bibliothèque.');
    } catch (error) {
      console.error('Erreur lors de la suppression de l’image générée:', error);
      setSubmitError(`Erreur lors de la suppression : ${error.message || 'erreur inconnue'}`);
    } finally {
      setGeneratedImageDeletionId(null);
    }
  };

  // Copier une image du produit vers les images générées
  // Permet de sauvegarder une copie d'une image produit dans la galerie des images générées par IA
  const copyProductImageToGenerated = async (image) => {
    // Récupérer l'URL de l'image (previewUrl pour les nouvelles, existingUrl ou image_url pour les existantes)
    const imageUrl = image.existingUrl || image.image_url || image.previewUrl;

    if (!imageUrl) {
      setSubmitError("Impossible de copier cette image : URL non disponible.");
      return;
    }

    try {
      // Sauvegarder l'image dans la base de données comme image générée
      const { data } = await productsApi.saveGeneratedImage({
        prompt: image.alt_text || 'Image importée depuis le produit',
        imageUrl: imageUrl,
        model: 'imported', // Modèle spécial pour indiquer que c'est une image importée
        productId: isEditMode ? id : null
      });

      if (data && data.data) {
        // Ajouter l'image sauvegardée à la liste des images générées
        // L'image originale reste dans les images du produit
        setSavedGeneratedImages(prev => [...prev, data.data]);
      }
    } catch (error) {
      console.error('Erreur lors de la copie de l\'image:', error);
      setSubmitError(`Erreur lors de la copie: ${error.message}`);
    }
  };

  // Ajout manuel d'une image dans la galerie "Images générées par IA"
  const handleManualImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Réinitialiser l'input pour permettre de re-sélectionner le même fichier
    e.target.value = '';

    setManualUploadLoading(true);
    try {
      // Convertir le fichier en data URL base64
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Sauvegarder via l'API (upload sur Supabase Storage + enregistrement en BDD)
      const { data } = await productsApi.saveGeneratedImage({
        prompt: file.name,
        imageUrl: dataUrl,
        model: 'manual',
        productId: isEditMode ? id : null
      });

      if (data && data.data) {
        setSavedGeneratedImages(prev => [...prev, data.data]);
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload manuel:', error);
      setSubmitError(`Erreur lors de l'upload: ${error.message}`);
    } finally {
      setManualUploadLoading(false);
    }
  };

  // Obtenir tous les variants qui correspondent à une valeur d'option
  const getVariantsForOptionValue = (optionName, optionValue) => {
    return formData.variations.filter(variant =>
      variant.options && variant.options[optionName] === optionValue
    );
  };

  // Ouvrir le modal de sélection d'images pour un variant
  const openImageSelectionModal = (variantId, variantTitle) => {
    const variant = formData.variations.find(v => v.id === variantId);
    setImageSelectionModal({
      isOpen: true,
      variant,
      mode: 'variant'
    });
  };

  // Fermer le modal de sélection
  const closeImageSelectionModal = () => {
    setImageSelectionModal({
      isOpen: false,
      variant: null,
      mode: 'variant'
    });
  };

  // Associer une image à une valeur d'option spécifique
  const associateImageToOptionValue = (imageId, optionName, optionValue) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => {
        if (img.id === imageId) {
          const newOptionValues = { ...img.option_values } || {};
          newOptionValues[optionName] = optionValue;
          return { ...img, option_values: newOptionValues };
        }
        return img;
      })
    }));
  };

  // Dissocier une image d'une valeur d'option
  const dissociateImageFromOptionValue = (imageId, optionName) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => {
        if (img.id === imageId && img.option_values) {
          const newOptionValues = { ...img.option_values };
          delete newOptionValues[optionName];
          return {
            ...img,
            option_values: Object.keys(newOptionValues).length > 0 ? newOptionValues : null
          };
        }
        return img;
      })
    }));
  };

  // Vérifier si une image est associée à une valeur d'option spécifique du variant
  // const isImageAssociatedToVariant = (imageId, variant) => {
  //   const image = formData.images.find(img => img.id === imageId);
  //   if (!image?.option_values || !variant?.options) return false;

  //   // Vérifier si l'image correspond à au moins une valeur d'option du variant
  //   return Object.entries(image.option_values).some(([optionName, optionValue]) => {
  //     return variant.options[optionName] === optionValue;
  //   });
  // };
  // --- Fin Gestion Images Multiples ---

  // --- Gestion des Tags ---
  const [currentTag, setCurrentTag] = useState('');

  const addTag = (tagName) => {
    const trimmedTag = tagName.trim().toLowerCase();
    if (trimmedTag && !formData.tags.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, trimmedTag]
      }));
    }
    setCurrentTag('');
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(currentTag);
    } else if (e.key === 'Backspace' && !currentTag && formData.tags.length > 0) {
      // Supprimer le dernier tag si l'input est vide et on appuie sur backspace
      const lastTag = formData.tags[formData.tags.length - 1];
      removeTag(lastTag);
    }
  };

  // Tags prédéfinis suggérés basés sur la catégorie e-commerce
  // (Fonction importée depuis constants/productCategories.js)
  const getSuggestedTagsForCategory = () => {
    return getSuggestedTags(formData.category, formData.tags);
  };
  // --- Fin Gestion des Tags ---


  // Fonctions pour l'insertion d'images via URL
  const handleInsertImageUrl = (imageUrl, altText, dimensions) => {
    console.log('🖼️ Tentative d\'insertion d\'image:', { imageUrl, altText, dimensions });

    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      console.log('📝 Quill editor trouvé:', quill);

      const range = quill.getSelection() || { index: 0, length: 0 };
      console.log('📍 Position curseur:', range);

      try {
        // Insertion de l'image à la position du curseur
        quill.insertEmbed(range.index, 'image', imageUrl);
        console.log('✅ Image insérée avec succès');

        // Appliquer les dimensions personnalisées si fournies
        if (dimensions && (dimensions.width || dimensions.height)) {
          // Attendre un court moment que l'image soit insérée
          setTimeout(() => {
            const images = quill.root.querySelectorAll('img');
            const lastImage = images[images.length - 1]; // Dernière image insérée

            if (lastImage && lastImage.src === imageUrl) {
              if (dimensions.width) {
                lastImage.style.width = `${dimensions.width}px`;
              }
              if (dimensions.height) {
                lastImage.style.height = `${dimensions.height}px`;
              }

              // Appliquer object-fit pour maintenir les proportions si demandé
              if (dimensions.maintainAspectRatio) {
                lastImage.style.objectFit = 'contain';
              } else {
                lastImage.style.objectFit = 'fill';
              }

              console.log('📐 Dimensions appliquées:', {
                width: dimensions.width,
                height: dimensions.height,
                objectFit: dimensions.maintainAspectRatio ? 'contain' : 'fill'
              });

              // Forcer la mise à jour du contenu
              const newContent = quill.root.innerHTML;
              handleDescriptionChange(newContent);
            }
          }, 100);
        }

        // Déplacer le curseur après l'image
        quill.setSelection(range.index + 1);

        // Forcer la mise à jour de la description
        const newContent = quill.root.innerHTML;
        console.log('📄 Nouveau contenu HTML:', newContent);

        // Déclencher manuellement le changement pour mettre à jour le state
        handleDescriptionChange(newContent);

      } catch (error) {
        console.error('❌ Erreur lors de l\'insertion:', error);
      }
    } else {
      console.error('❌ QuillRef non disponible');
    }
  };

  const openImageUrlDialog = useCallback(() => {
    console.log('🔄 Ouverture dialogue image URL');
    setImageUrlDialog({
      isOpen: true,
      quillRef: quillRef.current
    });
  }, []);

  const closeImageUrlDialog = useCallback(() => {
    console.log('🔄 Fermeture dialogue image URL');
    setImageUrlDialog({
      isOpen: false,
      quillRef: null
    });
  }, []);

  // Configuration de l'éditeur WYSIWYG avec bouton d'image personnalisé
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'image-url'], // Ajout du bouton d'image personnalisé
        ['clean']
      ],
      handlers: {
        'image-url': openImageUrlDialog // Handler pour ouvrir la boîte de dialogue
      }
    }
  }), [openImageUrlDialog]);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background', 'link', 'image'
  ];

  // Données de base - Catégories e-commerce générales
  // (Importées depuis constants/productCategories.js)

  // Réinitialiser l'état lors du changement de produit (navigation entre produits)
  useEffect(() => {
    if (isEditMode) {
      isMounted.current = false;
      // Nettoyer les erreurs et suggestions IA pour éviter la confusion entre produits
      setSubmitError(null);
      setAiSuggestions(null);
      setAiError(null);
      setAiApplySelection(createDefaultAiSelection());
    }
  }, [id, isEditMode]);

  // Chargement des données en mode édition
  useEffect(() => {
    const loadProductData = async () => {
      if (!isEditMode) {
        setIsLoading(false);
        isMounted.current = true;
        return;
      }

      try {
        setIsLoading(true);
        console.log('Chargement du produit pour édition, ID:', id);

        // Récupérer le produit depuis l'API
        const response = await productsApi.getById(id);
        console.log('Données produit récupérées:', response);

        if (response && response.data) {
          const product = response.data;
          console.log('Produit complet depuis l\'API:', product);
          console.log('Variations brutes depuis l\'API:', product.variations);

          // Remplir le formulaire avec les données existantes
          setFormData({
            name: product.name || '',
            sku: product.sku || '',
            category: product.category || '',
            subCategory: product.subCategory || '',
            price: product.price?.toString() || '',
            discountPrice: product.discountPrice?.toString() || '',
            status: product.status || 'active',
            stockLevel: product.stockLevel?.toString() || '',
            description: product.description || '',
            images: product.images ? product.images.map((img, index) => ({
              id: img.id || `existing_${index}`,
              existingUrl: img.image_url,
              alt_text: img.alt_text || '',
              position: img.position || index + 1,
              is_main: img.is_main || false,
              option_values: img.option_values || null
            })) : [],
            barcode: product.barcode || '',
            has_variants: product.has_variants || false,
            options: product.options || [],
            variations: product.variations ? product.variations.map(v => {
              console.log('Mappage variation:', v);

              // Fonction helper pour convertir en string de manière sûre
              const toStringOrDefault = (value, defaultValue = '') => {
                if (value === null || value === undefined || value === '') return defaultValue;
                return String(value);
              };

              // Normalise les valeurs d'options : si une valeur est un objet {name, value},
              // on extrait le nom (string) pour éviter l'erreur React #31 lors du rendu
              const normalizedOptions = Object.fromEntries(
                Object.entries(v.options || {}).map(([key, val]) => [
                  key,
                  typeof val === 'object' && val !== null ? (val.name ?? val.value ?? String(val)) : val
                ])
              );

              const mappedVariation = {
                id: v.id,
                options: normalizedOptions,
                title: v.title || '',
                price: toStringOrDefault(v.price, ''),
                sku: v.sku || '',
                stock: toStringOrDefault(v.inventory_quantity, '0'),
                compare_at_price: toStringOrDefault(v.compare_at_price, ''),
                barcode: v.barcode || ''
              };
              console.log('Variation mappée:', mappedVariation);
              return mappedVariation;
            }) : [],
            tags: product.tags || []
          });

          // Reconstituer les productOptions si le produit a des variants
          if (product.has_variants && product.options) {
            const reconstructedOptions = product.options.map((opt, index) => {
              optionIdCounter++;
              return {
                id: `o${optionIdCounter}`,
                name: opt.name,
                values: [] // Sera rempli en analysant les variations
              };
            });

            // Extraire les valeurs d'options depuis les variations
            if (product.variations) {
              product.variations.forEach(variation => {
                if (variation.options) {
                  Object.entries(variation.options).forEach(([optionName, optionValue]) => {
                    const optionIndex = reconstructedOptions.findIndex(opt => opt.name === optionName);
                    if (optionIndex !== -1) {
                      const existingValue = reconstructedOptions[optionIndex].values.find(val => val.name === optionValue);
                      if (!existingValue) {
                        valueIdCounter++;
                        reconstructedOptions[optionIndex].values.push({
                          id: `v${valueIdCounter}`,
                          name: optionValue
                        });
                      }
                    }
                  });
                }
              });
            }

            setProductOptions(reconstructedOptions);
          }

          // Récupérer les images générées par IA de l'utilisateur
          if (product.generatedImages && product.generatedImages.length > 0) {
            console.log('Images générées récupérées:', product.generatedImages);
            setSavedGeneratedImages(product.generatedImages);
          } else {
            setSavedGeneratedImages([]);
          }
        }

        setIsLoading(false);
        isMounted.current = true;
      } catch (error) {
        console.error('Erreur lors du chargement du produit:', error);
        setSubmitError(`Erreur lors du chargement du produit: ${error.message}`);
        setIsLoading(false);
      }
    };

    loadProductData();
  }, [id, isEditMode]);

  useEffect(() => {
    let isCancelled = false;

    const fetchProviders = async () => {
      try {
        const { data } = await productsApi.getAiProviders();
        if (!isCancelled && data?.providers) {
          setAiProviders(data.providers);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des fournisseurs IA:', error);
      }
    };

    fetchProviders();
    return () => {
      isCancelled = true;
    };
  }, []);

  // Charger les option_definitions au montage pour l'autocomplete des noms d'options
  useEffect(() => {
    const fetchOptionDefinitions = async () => {
      try {
        const { data } = await productsApi.getOptionDefinitions();
        setOptionDefinitionSuggestions(data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des option_definitions:', error);
      }
    };
    fetchOptionDefinitions();
  }, []);

  useEffect(() => {
    if (!aiMenuOpen) return;

    const handleClickOutside = (event) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(event.target)) {
        setAiMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [aiMenuOpen]);

  // Gestionnaires de base
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({
      ...prev,
      description: content
    }));

    if (errors.description) {
      setErrors(prev => ({ ...prev, description: null }));
    }
  };

  const handleCancel = () => {
    // Naviguer vers la liste des produits en revenant sur la page d'origine
    navigate('/app/products', {
      state: { initialPage: returnPage }
    });
  };

  // Navigation vers le produit précédent
  const handlePreviousProduct = () => {
    if (!hasPrevious) return;
    const previousProductId = productIds[currentIndex - 1];
    navigate(`/app/products/${previousProductId}/edit`, {
      state: {
        ...navigationContext,
        currentIndex: currentIndex - 1
      },
      replace: true // Remplacer l'historique pour éviter d'accumuler les entrées
    });
  };

  // Navigation vers le produit suivant
  const handleNextProduct = () => {
    if (!hasNext) return;
    const nextProductId = productIds[currentIndex + 1];
    navigate(`/app/products/${nextProductId}/edit`, {
      state: {
        ...navigationContext,
        currentIndex: currentIndex + 1
      },
      replace: true // Remplacer l'historique pour éviter d'accumuler les entrées
    });
  };

  // Mémorise les seuls fournisseurs autorisés pour le pré-remplissage d'une
  // fiche produit. Cette liste aligne le menu sur les fournisseurs acceptés
  // par le serveur et évite d'afficher Gemini comme option utilisable.
  const providerOptions = useMemo(() => ([
    { id: 'chatgpt', label: 'ChatGPT' },
    { id: 'claude', label: 'Claude' }
  ]), []);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const getImagesPayload = useCallback(async () => {
    if (!formData.images || formData.images.length === 0) {
      return [];
    }

    // Prendre les 2 premières images
    // On priorise les images qui ne sont pas liées à des variantes spécifiques si possible,
    // mais sinon on prend juste les 2 premières par position.
    const sortedImages = [...formData.images].sort((a, b) => a.position - b.position);
    const targetImages = sortedImages.slice(0, 2);

    const payloads = [];

    for (const source of targetImages) {
      let payload = null;

      if (source?.file instanceof File) {
        const dataUrl = await fileToDataUrl(source.file);
        payload = { type: 'base64', data: dataUrl };
      } else if (source?.previewUrl) {
        payload = { type: 'base64', data: source.previewUrl };
      } else if (source?.existingUrl) {
        payload = { type: 'url', data: source.existingUrl };
      } else if (source?.image_url) {
        payload = { type: 'url', data: source.image_url };
      } else if (typeof source === 'string') {
        payload = { type: source.startsWith('data:') ? 'base64' : 'url', data: source };
      }

      if (payload) {
        payloads.push(payload);
      }
    }

    return payloads;
  }, [formData.images]);

  const handleAutoFill = useCallback(async (providerKey) => {
    const selectedProvider = aiProviders?.[providerKey];
    console.log('selectedProvider', selectedProvider);
    if (!selectedProvider?.available || aiLoadingProvider) {
      return;
    }

    setAiError(null);
    let payloads = [];

    try {
      payloads = await getImagesPayload();
      console.log('payloads', payloads);
    } catch (error) {
      console.error('Erreur lors de la préparation des images:', error);
      setAiError('Impossible de lire les images sélectionnées.');
      return;
    }

    if (payloads.length === 0) {
      setAiError(isEditMode
        ? 'Aucune image existante détectée. Ajoutez au moins une image au produit.'
        : 'Ajoutez une image du produit avant d’utiliser le remplissage automatique.');
      return;
    }

    setAiLoadingProvider(providerKey);
    try {
      console.log('payloaddata', {
        provider: providerKey,
        images: payloads,
        context: {
          mode: isEditMode ? 'update' : 'create',
          existingName: formData.name,
          existingDescription: formData.description,
          existingSku: formData.sku,
          existingTags: formData.tags || []
        }
      });
      const { data } = await productsApi.requestAutoFill({
        provider: providerKey,
        images: payloads,
        context: {
          mode: isEditMode ? 'update' : 'create',
          existingName: formData.name,
          existingDescription: formData.description,
          existingSku: formData.sku,
          existingTags: formData.tags || []
        }
      });

      if (data?.suggestions) {
        setAiSuggestions({
          ...data.suggestions,
          provider: data.provider || providerKey
        });
        setAiApplySelection(buildSelectionFromSuggestions(data.suggestions));
        setAiMenuOpen(false);
      } else {
        setAiError('Réponse IA vide. Réessayez ou vérifiez la clé API.');
      }
    } catch (error) {
      console.error('Erreur IA:', error);
      const message = error.response?.data?.message || 'Erreur lors de la génération automatique.';
      setAiError(message);
    } finally {
      setAiLoadingProvider(null);
    }
  }, [aiProviders, aiLoadingProvider, formData, getImagesPayload, isEditMode]);

  /**
   * Convertit les sauts de ligne d'un texte brut en balises HTML <br>
   * Nécessaire pour préserver le formatage dans ReactQuill (éditeur WYSIWYG)
   * @param {string} text - Texte brut avec sauts de ligne (\n)
   * @returns {string} HTML avec balises <br> à la place des \n
   */
  const convertNewlinesToHtml = (text) => {
    if (!text) return '';
    // Remplacer les doubles sauts de ligne par des paragraphes
    // puis les simples sauts de ligne par des <br>
    return text
      .split('\n\n')
      .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
      .join('');
  };

  const handleApplyAiSuggestions = useCallback(() => {
    if (!aiSuggestions) return;

    const hasSelectedField = Object.entries(aiApplySelection).some(
      ([field, isSelected]) => isSelected && hasSuggestionValue(field)
    );

    if (!hasSelectedField) {
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev };

      if (aiApplySelection.name && aiSuggestions.name) {
        updated.name = aiSuggestions.name;
      }

      if (aiApplySelection.description && aiSuggestions.description) {
        // Convertir les sauts de ligne (\n) en HTML (<br>) pour ReactQuill
        updated.description = convertNewlinesToHtml(aiSuggestions.description);
      }

      if (aiApplySelection.sku && aiSuggestions.sku) {
        updated.sku = aiSuggestions.sku;
      }

      if (aiApplySelection.tags && Array.isArray(aiSuggestions.tags) && aiSuggestions.tags.length > 0) {
        updated.tags = mergeTagsWithoutDuplicates(prev.tags || [], aiSuggestions.tags);
      }

      return updated;
    });

    handleClearAiSuggestions();
    setAiError(null);
  }, [aiSuggestions, aiApplySelection, hasSuggestionValue, handleClearAiSuggestions]);

  // --- NanoBanana Handlers ---
  const handleOpenNanoBanana = () => {
    setIsNanoBananaOpen(true);
  };

  /**
   * Prépare les références puis délègue au serveur la rédaction du prompt par Luna
   * et la génération de l'image. Le prompt final est conservé pour l'historique.
   */
  const handleNanoBananaGenerate = async ({ prompt, images, productContext, creativeOptions, model, numberOfImages, generateFrontBack, quality, aspectRatio, onSuccess, saveImage }) => {
    // Mode sauvegarde d'une image spécifique
    if (saveImage) {
      setNanoBananaLoading(true);
      try {
        // Sauvegarder l'image avec le productId si on est en mode édition
        const { data } = await productsApi.saveGeneratedImage({
          prompt,
          imageUrl: saveImage,
          model,
          productId: isEditMode ? id : null // Lier l'image au produit si on édite un produit existant
        });

        if (data && data.data) {
          // Ajouter l'image sauvegardée à la liste des images générées
          // Elle apparaîtra dans la section "Images générées par IA"
          setSavedGeneratedImages(prev => [...prev, data.data]);

          // Ne pas ajouter directement aux images du produit
          // L'utilisateur devra cliquer sur "Ajouter" pour l'ajouter aux images du produit

          // Ne pas fermer le modal ici pour permettre de sauvegarder d'autres images
          // setIsNanoBananaOpen(false);
          // Feedback optionnel : on pourrait ajouter un toast ou une notification ici
        }
      } catch (error) {
        console.error('Erreur sauvegarde image:', error);
        setAiError(`Erreur lors de la sauvegarde: ${error.message}`);
      } finally {
        setNanoBananaLoading(false);
      }
      return;
    }

    // Mode génération
    setNanoBananaLoading(true);
    setAiError(null);

    try {
      // Préparation des images
      let payloads = [];
      if (images && images.length > 0) {
        for (const source of images) {
          let payload = null;
          if (source?.file instanceof File) {
            const dataUrl = await fileToDataUrl(source.file);
            payload = { type: 'base64', data: dataUrl };
          } else if (source?.previewUrl) {
            payload = { type: 'base64', data: source.previewUrl };
          } else if (source?.existingUrl) {
            payload = { type: 'url', data: source.existingUrl };
          } else if (source?.image_url) {
            payload = { type: 'url', data: source.image_url };
          }
          if (payload) payloads.push(payload);
        }
      }

      const { data } = await productsApi.generateImage({
        prompt,
        images: payloads,
        product: productContext,
        creativeOptions,
        model,
        numberOfImages,
        generateFrontBack,
        quality, // Transmis uniquement utilisé si model est GPT Image 2.5 Sunburst
        aspectRatio
      });

      if (data && data.imageUrls && data.imageUrls.length > 0) {
        if (onSuccess) {
          onSuccess(data.imageUrls, data.prompt, data.aiBalance);
        }
      } else {
        throw new Error('Aucune image retournée par le service.');
      }

    } catch (error) {
      console.error('Erreur NanoBanana:', error);
      setAiError(`Erreur génération image: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setNanoBananaLoading(false);
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom du produit est requis';
    if (!formData.sku.trim()) newErrors.sku = 'La référence est requise';
    if (!formData.category) newErrors.category = 'La catégorie est requise';

    if (formData.has_variants) {
      // Validation pour produits avec variations
      if (!formData.sku.trim()) newErrors.sku = 'La référence de base est requise pour générer les SKU des variations';

      // Validation des options
      if (productOptions.length === 0) {
        newErrors.options = 'Au moins une option est requise pour un produit avec variations.';
      } else {
        productOptions.forEach(opt => {
          if (!opt.name.trim()) newErrors[`optionName_${opt.id}`] = 'Le nom de l\'option ne peut pas être vide.';
          if (opt.values.length === 0) newErrors[`optionValues_${opt.id}`] = 'Au moins une valeur est requise pour cette option.';
        });
      }

      // Validation des variations générées
      if (formData.variations.length === 0 && productOptions.length > 0 && productOptions.every(opt => opt.values.length > 0)) {
        newErrors.variations = 'Aucune variation générée. Vérifiez vos options et le SKU de base.';
      } else {
        formData.variations.forEach((variation, index) => {
          if (!variation.price || isNaN(Number(variation.price)) || Number(variation.price) <= 0) {
            newErrors[`variation_${index}_price`] = 'Prix requis';
          }
          if (!variation.stock || isNaN(Number(variation.stock)) || Number(variation.stock) < 0) {
            newErrors[`variation_${index}_stock`] = 'Stock requis';
          }
        });
      }
    } else {
      // Validation pour produit simple
      if (!formData.price.trim() || isNaN(Number(formData.price)) || Number(formData.price) <= 0)
        newErrors.price = 'Prix valide requis';
      if (!formData.stockLevel.trim() || isNaN(Number(formData.stockLevel)) || Number(formData.stockLevel) < 0)
        newErrors.stockLevel = 'Stock valide requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Upload d'une image
  const uploadImage = async (file) => {
    if (!file) return null;
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `public/${fileName}`;
      let { error: uploadError } = await supabase.storage.from('product-images').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('product-images').getPublicUrl(filePath);
      if (!data || !data.publicUrl) throw new Error("URL publique non trouvée.");
      return data.publicUrl;
    } catch (error) {
      console.error("Erreur Upload:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Uploader toutes les nouvelles images
      const uploadedImages = [];

      for (const image of formData.images) {
        if (image.file) {
          const uploadedUrl = await uploadImage(image.file);
          if (!uploadedUrl) throw new Error(`Échec de l'upload de l'image ${image.alt_text}.`);

          uploadedImages.push({
            ...image,
            image_url: uploadedUrl,
            file: undefined,
            previewUrl: undefined
          });
        } else if (image.existingUrl) {
          uploadedImages.push({
            ...image,
            image_url: image.existingUrl
          });
        }
      }

      // 2. Préparer les données finales pour l'API
      const mainImage = uploadedImages.find(img => img.is_main && (!img.option_values || Object.keys(img.option_values).length === 0));

      // --- AJOUT : Génération automatique des tags depuis les variantes ---
      // On commence avec les tags existants saisis manuellement
      let finalTags = [...formData.tags];

      if (formData.has_variants && productOptions.length > 0) {
        // Créer un Set de tags normalisés pour vérification rapide des doublons existants
        const existingTagsSet = new Set(finalTags.map(t => t.toLowerCase().trim()));

        productOptions.forEach(option => {
          if (option.name && option.values.length > 0) {
            // Nettoyage du nom de l'option (minuscules, remplacement espaces par tirets)
            // ex: "Couleur Principale" devient "couleur-principale"
            const optionKey = option.name.trim().toLowerCase().replace(/\s+/g, '-');

            option.values.forEach(val => {
              if (val.name) {
                // Nettoyage de la valeur
                // ex: "Rouge Foncé" devient "rouge-foncé"
                const valueName = val.name.trim().toLowerCase().replace(/\s+/g, '-');
                const newTag = `${optionKey}-${valueName}`;

                // Ajout seulement si ce tag n'existe pas déjà
                if (!existingTagsSet.has(newTag)) {
                  finalTags.push(newTag);
                  existingTagsSet.add(newTag); // Marquer comme ajouté
                }
              }
            });
          }
        });
      }
      // --- FIN AJOUT ---

      const formattedData = {
        name: formData.name,
        sku: formData.sku,
        category: formData.category,
        subCategory: formData.subCategory,
        status: formData.status,
        description: formData.description,
        tags: finalTags, // Utilisation de la liste enrichie (manuels + variantes)
        barcode: formData.barcode || null,
        has_variants: formData.has_variants,
        mainImage: mainImage?.image_url || null,
        price: formData.has_variants ? null : Number(formData.price),
        discountPrice: formData.has_variants ? null : (formData.discountPrice ? Number(formData.discountPrice) : null),
        stockLevel: formData.has_variants ? null : Number(formData.stockLevel),
        options: formData.has_variants ? productOptions.map(opt => ({ name: opt.name })) : [],
        variations: formData.has_variants ? formData.variations.map((v, index) => ({
          id: typeof v.id === 'string' && v.id.startsWith('temp_') ? undefined : v.id,
          title: v.title,
          options: v.options,
          price: Number(v.price),
          inventory_quantity: Number(v.stock),
          sku: v.sku,
          compare_at_price: v.compare_at_price ? Number(v.compare_at_price) : null,
          barcode: v.barcode || null
        })) : [],
        images: uploadedImages.map(img => ({
          id: img.id || undefined,
          image_url: img.image_url,
          alt_text: img.alt_text,
          position: img.position,
          is_main: img.is_main,
          option_values: img.option_values || null
        }))
      };

      console.log(`Données formatées pour ${isEditMode ? 'Update' : 'Create'}:`, formattedData);

      // 3. Appeler l'API appropriée
      if (isEditMode) {
        console.log('Mise à jour du produit:', id, formattedData);
        const result = await updateProduct(id, formattedData);
        console.log("Réponse API Update:", result);
        window.location.reload();
      } else {
        const result = await createProduct(formattedData);
        console.log("Réponse API Create:", result);

        const createdProductId = result?.data?.productId;
        if (!createdProductId) {
          throw new Error('Le produit a été créé, mais son identifiant est introuvable.');
        }

        // Les images IA créées avant l'existence du produit sont temporairement
        // orphelines. Elles sont rattachées uniquement après obtention de son ID.
        const generatedImageIds = savedGeneratedImages.map((image) => image.id).filter(Boolean);
        if (generatedImageIds.length > 0) {
          await productsApi.attachGeneratedImagesToProduct({
            productId: createdProductId,
            imageIds: generatedImageIds
          });
        }

        // Ouvre immédiatement la fiche persistée afin que la galerie soit relue
        // depuis la base avec les images IA nouvellement liées.
        navigate(`/app/products/${createdProductId}/edit`, { replace: true });
      }
      // Rediriger vers la liste des produits en revenant sur la page d'origine
      // navigate('/app/products', {
      //   state: { initialPage: returnPage }
      // });

    } catch (error) {
      console.error(`Erreur lors de ${isEditMode ? 'la mise à jour' : 'la création'}:`, error);
      setSubmitError(error.message || `Une erreur est survenue lors de ${isEditMode ? 'la mise à jour' : 'la création'}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canApplyAiSuggestions = aiSuggestions
    ? Object.entries(aiApplySelection).some(
      ([field, selected]) => selected && hasSuggestionValue(field)
    )
    : false;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mb-4"></div>
          <p className="text-lg text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <div className="sticky top-[5px] z-30 bg-white shadow-sm border-b border-gray-200 sm:top-[61px] md:top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sous 2xl, les informations et les actions se répartissent sur deux lignes pour éviter tout chevauchement. */}
          <div className="flex flex-wrap items-center gap-x-2 py-0 sm:flex-col sm:flex-nowrap sm:items-stretch sm:gap-x-0 sm:gap-y-3 sm:py-3 2xl:h-16 2xl:flex-row 2xl:items-center 2xl:justify-between 2xl:gap-4 2xl:py-0">
            {/* Mobile : une seule ligne (retour icône + titre tronqué + navigation) ; le texte du retour apparaît dès sm */}
            <div className="order-2 flex min-w-0 flex-1 items-center gap-x-3 py-2 sm:order-none sm:flex-none sm:gap-x-4 sm:py-0">
              <button
                onClick={handleCancel}
                aria-label="Retour aux produits"
                className="inline-flex flex-shrink-0 items-center text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-5 w-5 sm:mr-2" />
                <span className="hidden sm:inline">Retour aux produits</span>
              </button>
              <div className="hidden h-6 border-l border-gray-300 sm:block"></div>
              <h1 className="min-w-0 truncate text-lg font-semibold text-gray-900 sm:text-xl">
                {isEditMode ? 'Modifier le produit' : 'Nouveau produit'}
              </h1>

              {/* Boutons de navigation entre produits (seulement en mode édition avec contexte) */}
              {isEditMode && productIds.length > 0 && (
                <>
                  <div className="hidden h-6 border-l border-gray-300 sm:block"></div>
                  <div className="ml-auto flex flex-shrink-0 items-center space-x-2 sm:ml-0">
                    <button
                      onClick={handlePreviousProduct}
                      disabled={!hasPrevious}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Produit précédent"
                    >
                      <ArrowLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-500">
                      {currentIndex + 1} / {productIds.length}
                    </span>
                    <button
                      onClick={handleNextProduct}
                      disabled={!hasNext}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Produit suivant"
                    >
                      <ArrowUpIcon className="h-4 w-4 transform rotate-90" />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Actions du header */}
            <div className="contents sm:flex sm:w-full sm:flex-row sm:flex-wrap sm:items-center sm:justify-end 2xl:w-auto 2xl:flex-nowrap 2xl:gap-3">
              {/* Boutons IA : 2 colonnes égales sur mobile */}
              <div className="relative order-first grid h-14 w-full grid-cols-2 content-center gap-2 sm:order-none sm:flex sm:h-auto sm:w-auto sm:gap-3" ref={aiMenuRef}>
                <button
                  type="button"
                  onClick={() => setAiMenuOpen(prev => !prev)}
                  disabled={aiLoadingProvider !== null}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-primary-200 shadow-sm text-sm font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {aiLoadingProvider ? 'Analyse en cours...' : 'Remplissage auto'}
                </button>
                <button
                  type="button"
                  onClick={handleOpenNanoBanana}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-yellow-300 shadow-sm text-sm font-medium rounded-md text-yellow-800 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Générer une image IA
                </button>
                {aiMenuOpen && (
                  <div className="absolute left-0 top-full mt-2 w-full max-w-xs sm:left-auto sm:right-0 sm:w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">Choisir une IA</p>
                      <p className="text-xs text-gray-500">Les options sans clé sont grisées.</p>
                    </div>
                    <div className="py-2">
                      {providerOptions.map((option) => {
                        const available = aiProviders?.[option.id]?.available;
                        const isActive = aiLoadingProvider === option.id;

                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => handleAutoFill(option.id)}
                            disabled={!available || isActive}
                            className={`w-full px-4 py-2 flex items-center justify-between text-sm ${available
                              ? 'text-gray-800 hover:bg-primary-50'
                              : 'text-gray-400 cursor-not-allowed'
                              }`}
                          >
                            <span>{option.label}</span>
                            <span className="text-xs">
                              {isActive ? '...' : (available ? 'Disponible' : 'Clé manquante')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {/* Les actions de validation restent ensemble, même lorsque les boutons IA passent à la ligne. */}
              <div className="order-3 flex items-center gap-2 sm:order-none sm:ml-auto">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="hidden sm:inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:bg-primary-400"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Sauvegarde...
                    </>
                  ) : (
                    isEditMode ? 'Mettre à jour' : 'Créer le produit'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {submitError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <p className="font-medium">Erreur:</p>
            <p>{submitError}</p>
          </div>
        )}
        {aiError && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
            <p className="text-sm">{aiError}</p>
          </div>
        )}
        {aiSuggestions && (
          <div className="mb-6 bg-white border border-primary-200 rounded-lg shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <p className="text-xs uppercase font-semibold text-primary-600 tracking-wider">
                  Proposition IA · {aiSuggestions.provider?.toUpperCase()}
                </p>
                <p className="text-base text-gray-700 mt-1">
                  Vérifiez la proposition détectée à partir de l'image et appliquez-la si elle correspond.
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={handleClearAiSuggestions}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  Ignorer
                </button>
                <button
                  type="button"
                  onClick={handleApplyAiSuggestions}
                  disabled={isSubmitting || !canApplyAiSuggestions}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Remplir les champs
                </button>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Champs à appliquer</p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AI_FIELD_OPTIONS.map((option) => {
                  const hasValue = hasSuggestionValue(option.key);
                  return (
                    <label
                      key={option.key}
                      className={`flex items-center space-x-2 text-sm ${hasValue ? 'text-gray-700' : 'text-gray-400'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={hasValue ? aiApplySelection[option.key] : false}
                        disabled={!hasValue}
                        onChange={() => handleToggleAiField(option.key)}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:bg-gray-100"
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase">Nom proposé</p>
                <p className="mt-2 text-sm font-medium text-gray-900">{aiSuggestions.name || '—'}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase">SKU suggéré</p>
                <p className="mt-2 text-sm font-mono text-gray-900">{aiSuggestions.sku || '—'}</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase">Description</p>
              <p className="mt-2 text-sm text-gray-800 whitespace-pre-line">
                {aiSuggestions.description || '—'}
              </p>
            </div>
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Tags</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Array.isArray(aiSuggestions.tags) && aiSuggestions.tags.length > 0 ? (
                  aiSuggestions.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">Aucun tag proposé</span>
                )}
              </div>
            </div>
          </div>
        )}

        <form className="space-y-8">
          {/* Informations générales */}
          <div className="bg-white shadow-sm rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Informations générales</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                {/* Nom du produit */}
                <div className="sm:col-span-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Nom du produit *
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`}
                      placeholder="Nom du produit"
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>
                </div>

                {/* SKU */}
                <div className="sm:col-span-2">
                  <label htmlFor="sku" className="block text-sm font-medium text-gray-700">
                    Référence (SKU) {formData.has_variants ? 'base' : ''} *
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="sku"
                      id="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.sku ? 'border-red-300' : ''}`}
                      placeholder="SKU-001"
                    />
                    {errors.sku && <p className="mt-1 text-sm text-red-600">{errors.sku}</p>}
                  </div>
                </div>

                {/* Catégorie */}
                <div className="sm:col-span-3">
                  <label htmlFor="category" className="flex items-center text-sm font-medium text-gray-700">
                    <CubeIcon className="h-4 w-4 mr-2 text-gray-500" />
                    Catégorie *
                  </label>
                  <div className="mt-1 relative">
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${formData.category ? 'pl-10' : ''} ${errors.category ? 'border-red-300' : ''}`}
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    {/* Icône de la catégorie sélectionnée */}
                    {formData.category && (
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {getCategoryIcon(formData.category)}
                      </div>
                    )}
                    {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
                  </div>
                </div>

                {/* Sous-catégorie */}
                <div className="sm:col-span-3">
                  <label htmlFor="subCategory" className="flex items-center text-sm font-medium text-gray-700">
                    <TagIcon className="h-4 w-4 mr-2 text-gray-500" />
                    Sous-catégorie
                    {formData.category && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({subCategories[formData.category]?.length || 0} options)
                      </span>
                    )}
                  </label>
                  <div className="mt-1">
                    <select
                      id="subCategory"
                      name="subCategory"
                      value={formData.subCategory}
                      onChange={handleChange}
                      disabled={!formData.category}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="">
                        {!formData.category
                          ? "Sélectionnez d'abord une catégorie"
                          : "Sélectionnez une sous-catégorie"
                        }
                      </option>
                      {formData.category && subCategories[formData.category]?.map(subCategory => (
                        <option key={subCategory} value={subCategory}>
                          {subCategory}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Aperçu des sous-catégories quand une catégorie est sélectionnée */}
                  {formData.category && !formData.subCategory && subCategories[formData.category] && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-600 mb-2">Sous-catégories disponibles :</p>
                      <div className="flex flex-wrap gap-1">
                        {subCategories[formData.category].slice(0, 6).map((sub, index) => (
                          <span key={index} className="inline-block px-2 py-1 text-xs bg-white border border-gray-200 rounded text-gray-700">
                            {sub}
                          </span>
                        ))}
                        {subCategories[formData.category].length > 6 && (
                          <span className="inline-block px-2 py-1 text-xs text-gray-500">
                            +{subCategories[formData.category].length - 6} autres...
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description avec éditeur WYSIWYG */}
                <div className="sm:col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <div className="border border-gray-300 rounded-md">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={formData.description}
                      onChange={handleDescriptionChange}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Décrivez votre produit en détail... Utilisez le bouton d'image pour insérer des images via URL."
                      style={{ minHeight: '150px' }}
                    />
                  </div>
                  {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                </div>

                {/* Tags */}
                <div className="sm:col-span-6">
                  <label htmlFor="tags" className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <TagIcon className="h-4 w-4 mr-2 text-gray-500" />
                    Tags et mots-clés
                  </label>
                  <div className="space-y-3">
                    {/* Tags actuels et input */}
                    <div className="border border-gray-300 rounded-md p-3 min-h-[3rem] focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500">
                      <div className="flex flex-wrap gap-2 items-center">
                        {/* Tags existants */}
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-primary-400 hover:bg-primary-600 hover:text-white focus:outline-none focus:bg-primary-600 focus:text-white transition-colors"
                            >
                              <span className="sr-only">Supprimer le tag</span>
                              <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                              </svg>
                            </button>
                          </span>
                        ))}

                        {/* Input pour nouveaux tags */}
                        <input
                          type="text"
                          value={currentTag}
                          onChange={(e) => setCurrentTag(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          placeholder={formData.tags.length === 0 ? "Ajouter des tags (Entrée pour valider)" : "Ajouter un tag..."}
                          className="flex-1 min-w-[120px] border-none focus:ring-0 focus:outline-none text-sm placeholder-gray-400 bg-transparent"
                        />
                      </div>
                    </div>

                    {/* Tags suggérés */}
                    {formData.category && getSuggestedTagsForCategory().length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Tags suggérés pour {formData.category}:</p>
                        <div className="flex flex-wrap gap-2">
                          {getSuggestedTagsForCategory().slice(0, 8).map((suggestedTag) => (
                            <button
                              key={suggestedTag}
                              type="button"
                              onClick={() => addTag(suggestedTag)}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-800 transition-colors cursor-pointer"
                            >
                              <PlusIcon className="h-3 w-3 mr-1" />
                              {suggestedTag}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      Les tags aident à organiser et retrouver vos produits. Appuyez sur Entrée pour ajouter un tag.
                    </p>
                  </div>
                </div>

                {/* Statut */}
                <div className="sm:col-span-2">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Statut
                  </label>
                  <div className="mt-1">
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                      <option value="draft">Brouillon</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Images du Produit */}
          <div className="bg-white shadow-sm rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Images du produit</h2>
            </div>
            <div className="p-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <p className="text-sm text-gray-600">
                  Ajoutez des images pour présenter votre produit. La première image sera utilisée comme image principale.
                </p>
                <label
                  htmlFor="add-product-image"
                  className="cursor-pointer whitespace-nowrap text-center bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4 inline-block mr-2" />
                  Ajouter des images
                  <input
                    id="add-product-image"
                    type="file"
                    accept="image/*"
                    multiple
                    className="sr-only"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        addMultipleProductImages(e.target.files);
                      }
                      e.target.value = '';
                    }}
                    disabled={isSubmitting}
                  />
                </label>
              </div>

              {/* Grille d'images */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {getProductImages().map((image, index, array) => (
                  <div
                    key={image.id}
                    className={`relative group cursor-move transition-all duration-200 ${draggedImageId === image.id ? 'opacity-40 scale-95 ring-2 ring-primary-500' : ''}`}
                    draggable={!isSubmitting}
                    onDragStart={(e) => handleDragStart(e, image.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, image.id)}
                  >
                    <div className="aspect-square rounded-lg border border-gray-200 overflow-hidden bg-gray-50 hover:border-primary-400 transition-colors">
                      <img
                        src={image.previewUrl || image.existingUrl || image.image_url}
                        alt={image.alt_text}
                        className="h-full w-full object-cover pointer-events-none select-none" // Empêcher le drag natif de l'image elle-même pour utiliser celui du conteneur
                      />
                    </div>

                    {/* Badge image principale */}
                    {image.is_main && (
                      <div className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded shadow-sm z-10">
                        Principale
                      </div>
                    )}

                    {/* Actions (Aperçu / Supprimer / Mettre en principal / Copier vers généré) */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex space-x-1 z-10">
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); openProductImagePreview(image); }}
                        className="bg-gray-800 bg-opacity-75 text-white p-1 rounded hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-white"
                        title="Voir l'image en grand"
                        aria-label="Voir l'image en grand"
                      >
                        <EyeIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {!image.is_main && (
                        <button
                          type="button"
                          onClick={() => setMainImage(image.id)}
                          className="bg-gray-800 bg-opacity-75 text-white p-1 rounded hover:bg-opacity-100"
                          title="Définir comme image principale"
                        >
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18l3.09-6.26L18 10l-4.91-1.74L10 2 6.91 8.26 2 10l4.91 1.74L10 18z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {/* Bouton pour copier l'image vers les images générées */}
                      <button
                        type="button"
                        onClick={() => copyProductImageToGenerated(image)}
                        className="bg-yellow-500 bg-opacity-75 text-white p-1 rounded hover:bg-opacity-100"
                        title="Copier vers les images générées"
                      >
                        <GiftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        className="bg-red-600 bg-opacity-75 text-white p-1 rounded hover:bg-opacity-100"
                        title="Supprimer l'image"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Badge Variante (si liée à des options) */}
                    {image.option_values && Object.keys(image.option_values).length > 0 && (
                      <div className="absolute bottom-10 left-1 right-1 flex flex-wrap gap-1 justify-center pointer-events-none z-10">
                        {Object.entries(image.option_values).map(([key, val]) => (
                          <span key={key} className="bg-purple-600 bg-opacity-90 text-white text-[10px] px-1.5 py-0.5 rounded shadow-sm truncate max-w-full">
                            {val}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Contrôles de position (Déplacer Gauche / Droite) */}
                    <div className="absolute bottom-2 left-0 right-0 px-2 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      {/* Bouton Gauche (si pas le premier) */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveImage(image.id, 'left'); }}
                        className={`bg-white bg-opacity-90 text-gray-700 p-1 rounded-full shadow hover:bg-primary-50 hover:text-primary-600 transition-colors ${index === 0 ? 'invisible' : ''}`}
                        title="Déplacer vers la gauche"
                      >
                        <ArrowLeftIcon className="h-4 w-4" />
                      </button>

                      {/* Position actuelle */}
                      <span className="bg-gray-800 bg-opacity-75 text-white text-xs px-2 py-1 rounded self-center">
                        {index + 1}
                      </span>

                      {/* Bouton Droite (si pas le dernier) */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveImage(image.id, 'right'); }}
                        className={`bg-white bg-opacity-90 text-gray-700 p-1 rounded-full shadow hover:bg-primary-50 hover:text-primary-600 transition-colors ${index === array.length - 1 ? 'invisible' : ''}`}
                        title="Déplacer vers la droite"
                      >
                        <ArrowRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Placeholder si aucune image */}
                {getProductImages().length === 0 && (
                  <div className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 col-span-2">
                    <div className="text-center">
                      <PhotoIcon className="h-12 w-12 mx-auto mb-2" />
                      <p className="text-sm">Aucune image</p>
                      <p className="text-xs">Cliquez sur "Ajouter des images"</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modale de prévisualisation des images déjà rattachées au produit. */}
          {previewProductImage && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90 p-4"
              onClick={() => setPreviewProductImage(null)}
              role="dialog"
              aria-modal="true"
              aria-label="Aperçu de l'image du produit"
            >
              <div className="relative max-h-[90vh] max-w-5xl" onClick={(event) => event.stopPropagation()}>
                <img src={previewProductImage.src} alt={previewProductImage.alt} className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl" />
                <button
                  type="button"
                  onClick={() => setPreviewProductImage(null)}
                  className="mx-auto mt-4 block rounded-full bg-white px-6 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}

          {/* Images générées par IA — toujours visible, permet l'ajout manuel */}
          <div className="bg-white shadow-sm rounded-lg">
            {/* Input file caché pour l'upload manuel */}
            <input
              ref={manualUploadInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleManualImageUpload}
            />
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                  <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <span className="text-2xl">🍌</span> Images générées par IA
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Images NanoBanana ou ajoutées manuellement. Cliquez pour les ajouter au produit.
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {savedGeneratedImages.length > 0 && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {savedGeneratedImages.length} image{savedGeneratedImages.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {/* Bouton d'ajout manuel */}
                  <button
                    type="button"
                    onClick={() => manualUploadInputRef.current?.click()}
                    disabled={manualUploadLoading}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {manualUploadLoading ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5 text-gray-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Chargement...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-3.5 w-3.5" />
                        Ajouter une image
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {generatedImagesFeedback && (
                <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status" aria-live="polite" aria-atomic="true">
                  {generatedImagesFeedback}
                </div>
              )}
              {savedGeneratedImages.length === 0 ? (
                /* État vide */
                <div
                  className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors"
                  onClick={() => manualUploadInputRef.current?.click()}
                >
                  <PlusIcon className="h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">Cliquez pour ajouter une image</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {savedGeneratedImages.map((genImage) => (
                    <div key={genImage.id} className="relative group">
                      <div
                        className="aspect-square rounded-lg border border-gray-200 overflow-hidden bg-gray-50 hover:border-primary-500 transition-colors cursor-pointer"
                        onClick={() => setPreviewGeneratedImage(genImage)}
                      >
                        <img
                          src={genImage.image_url}
                          alt={genImage.prompt || 'Image générée'}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      {/* z-20 garde la suppression au-dessus de l'overlay (z-10) pendant le survol. */}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteGeneratedImage(genImage);
                        }}
                        disabled={generatedImageDeletionId === genImage.id}
                        aria-label={`Supprimer l'image générée du ${new Date(genImage.created_at).toLocaleDateString('fr-FR')}`}
                        title="Supprimer de la bibliothèque"
                        className="absolute right-2 top-2 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {generatedImageDeletionId === genImage.id ? (
                          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : <TrashIcon className="h-4 w-4" aria-hidden="true" />}
                      </button>

                      {/* Badge du modèle */}
                      <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs px-2 py-1 rounded font-medium">
                        {genImage.model_version === 'openai/gpt-image-2.5/sunburst/text-to-image' ? 'GPT 2.5'
                          : genImage.model_version === 'gpt-image-2' ? 'GPT-2'
                          : genImage.model_version === 'nano-banana-2-fast' ? 'NB Fast'
                            : genImage.model_version === 'nano-banana-2' ? 'NB 2'
                            : genImage.model_version === 'nano-banana-pro' ? 'NB Pro'
                              : genImage.model_version === 'manual' ? 'Manuel'
                                : genImage.model_version === 'imported' ? 'Importé'
                                  : 'IA'}
                      </div>

                      {/* Action d'ajout */}
                      <div className="absolute inset-0 z-10 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center pointer-events-none">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            addGeneratedImageToProduct(genImage);
                          }}
                          className="pointer-events-auto opacity-0 group-hover:opacity-100 transform scale-90 group-hover:scale-100 transition-all bg-white text-primary-600 px-4 py-2 rounded-lg shadow-lg font-medium text-sm hover:bg-primary-50 flex items-center gap-2"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Ajouter
                        </button>
                      </div>

                      {/* Info prompt au survol */}
                      {genImage.prompt && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <p className="text-white text-xs line-clamp-2" title={genImage.prompt}>
                            {genImage.prompt}
                          </p>
                        </div>
                      )}

                      {/* Date de création */}
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 text-center">
                          {new Date(genImage.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Info supplémentaire */}
              {savedGeneratedImages.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    💡 <strong>Astuce :</strong> Cliquez sur une image pour l'agrandir. Vous pouvez les ajouter directement à votre produit en cliquant sur le bouton "Ajouter".
                  </p>
                </div>
              )}
            </div>

            {/* Modal Lightbox pour prévisualisation */}
            {previewGeneratedImage && (
              <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90"
                onClick={() => setPreviewGeneratedImage(null)}
              >
                <div className="relative max-w-5xl max-h-[90vh] p-4 flex flex-col items-center" onClick={e => e.stopPropagation()}>
                  {/* Image agrandie */}
                  <img
                    src={previewGeneratedImage.image_url}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
                    alt={previewGeneratedImage.prompt || 'Image générée'}
                  />

                  {/* Informations de l'image */}
                  <div className="mt-6 w-full max-w-2xl bg-gray-900 bg-opacity-80 rounded-lg p-4 text-white">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                            {previewGeneratedImage.model_version === 'openai/gpt-image-2.5/sunburst/text-to-image' ? 'GPT Image 2.5 Sunburst'
                              : previewGeneratedImage.model_version === 'gpt-image-2' ? 'GPT Image 2'
                              : previewGeneratedImage.model_version === 'nano-banana-2-fast' ? 'NanoBanana 2 Fast'
                                : previewGeneratedImage.model_version === 'nano-banana-2' ? 'NanoBanana 2'
                                : previewGeneratedImage.model_version === 'nano-banana-pro' ? 'NanoBanana Pro'
                                  : previewGeneratedImage.model_version === 'manual' ? 'Ajout manuel'
                                    : previewGeneratedImage.model_version === 'imported' ? 'Importé'
                                      : 'IA'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(previewGeneratedImage.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        {previewGeneratedImage.prompt && (
                          <div>
                            <p className="text-sm font-medium text-gray-300 mb-1">Prompt :</p>
                            <p className="text-sm text-gray-200">{previewGeneratedImage.prompt}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="mt-4 flex justify-center gap-4">
                    <button
                      onClick={() => setPreviewGeneratedImage(null)}
                      className="px-6 py-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors"
                    >
                      Fermer
                    </button>
                    <button
                      onClick={() => {
                        addGeneratedImageToProduct(previewGeneratedImage);
                        setPreviewGeneratedImage(null);
                      }}
                      className="px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-500 transition-colors font-medium flex items-center gap-2"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Ajouter au produit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteGeneratedImage(previewGeneratedImage)}
                      disabled={generatedImageDeletionId === previewGeneratedImage.id}
                      className="px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-500 transition-colors font-medium flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <TrashIcon className="w-5 h-5" aria-hidden="true" />
                      {generatedImageDeletionId === previewGeneratedImage.id ? 'Suppression…' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Prix et Stock */}
          <div className="bg-white shadow-sm rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              {/* Mobile : titre puis case à cocher en dessous */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-medium text-gray-900">Prix et stock</h2>
                <div className="flex items-start sm:items-center">
                  <input
                    id="has_variants"
                    name="has_variants"
                    type="checkbox"
                    checked={formData.has_variants}
                    onChange={handleChange}
                    className="focus:ring-primary-500 h-4 w-4 mt-0.5 sm:mt-0 flex-shrink-0 text-primary-600 border-gray-300 rounded mr-2"
                  />
                  <label htmlFor="has_variants" className="text-sm font-medium text-gray-700">
                    Ce produit a plusieurs variations (taille, couleur, etc.)
                  </label>
                </div>
              </div>
            </div>
            <div className="p-6">
              {!formData.has_variants ? (
                // Section Prix/Stock pour produit simple
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
                  {/* Prix */}
                  <div className="sm:col-span-2">
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      Prix ({getDeviseActive()}) *
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        name="price"
                        id="price"
                        value={formData.price}
                        onChange={handleChange}
                        className={`focus:ring-primary-500 focus:border-primary-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md ${errors.price ? 'border-red-300' : ''}`}
                        placeholder="0"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">{getDeviseActive()}</span>
                      </div>
                    </div>
                    {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
                  </div>

                  {/* Prix promotionnel */}
                  <div className="sm:col-span-2">
                    <label htmlFor="discountPrice" className="block text-sm font-medium text-gray-700">
                      Prix promotionnel ({getDeviseActive()})
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        name="discountPrice"
                        id="discountPrice"
                        value={formData.discountPrice}
                        onChange={handleChange}
                        className="focus:ring-primary-500 focus:border-primary-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md"
                        placeholder="0"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">{getDeviseActive()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Stock */}
                  <div className="sm:col-span-2">
                    <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700">
                      Stock disponible *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="stockLevel"
                        id="stockLevel"
                        value={formData.stockLevel}
                        onChange={handleChange}
                        className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.stockLevel ? 'border-red-300' : ''}`}
                        placeholder="0"
                      />
                      {errors.stockLevel && <p className="mt-1 text-sm text-red-600">{errors.stockLevel}</p>}
                    </div>
                  </div>

                  {/* Code-barres */}
                  <div className="sm:col-span-6">
                    <label htmlFor="barcode" className="block text-sm font-medium text-gray-700">
                      Code-barres (EAN, UPC, etc.)
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="barcode"
                        id="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Code-barres optionnel"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                // Section Options et Variations
                <div className="space-y-8">
                  {/* Section Options du produit */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Options du produit</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Définissez les options (couleur, taille, etc.) pour créer automatiquement les variations de votre produit.
                    </p>

                    <div className="space-y-4">
                      {productOptions.map((option, index) => (
                        <div key={option.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <label htmlFor={`optionName_${option.id}`} className="block text-sm font-medium text-gray-700">
                              Option {index + 1}
                            </label>
                            <button
                              type="button"
                              onClick={() => removeOptionType(option.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                              title="Supprimer cette option"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>

                          {/* Input avec autocomplete sur les option_definitions existantes */}
                          <input
                            type="text"
                            id={`optionName_${option.id}`}
                            list={`optionDefList_${option.id}`}
                            value={option.name}
                            onChange={(e) => handleOptionNameChange(option.id, e.target.value)}
                            placeholder="Nom de l'option (ex: Couleur, Taille)"
                            className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md mb-3 ${errors[`optionName_${option.id}`] ? 'border-red-300' : ''}`}
                          />
                          {/* Datalist des noms d'options connus */}
                          <datalist id={`optionDefList_${option.id}`}>
                            {optionDefinitionSuggestions.map(def => (
                              <option key={def.id} value={def.name} />
                            ))}
                          </datalist>
                          {errors[`optionName_${option.id}`] && <p className="mt-1 text-sm text-red-600">{errors[`optionName_${option.id}`]}</p>}

                          {/* Valeurs de l'option */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Valeurs de l'option
                            </label>
                            {errors[`optionValues_${option.id}`] && <p className="mb-2 text-sm text-red-600">{errors[`optionValues_${option.id}`]}</p>}

                            {/* Tags des valeurs existantes */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              {option.values.map(value => (
                                <span key={value.id} className="inline-flex items-center py-1 pl-3 pr-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                                  {value.name}
                                  <button
                                    type="button"
                                    onClick={() => removeOptionValue(option.id, value.id)}
                                    className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-primary-400 hover:bg-primary-200 hover:text-primary-500 focus:outline-none focus:bg-primary-600 focus:text-white"
                                    title="Supprimer cette valeur"
                                  >
                                    <span className="sr-only">Supprimer</span>
                                    <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                      <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                                    </svg>
                                  </button>
                                </span>
                              ))}
                            </div>

                            {/* Input pour ajouter de nouvelles valeurs */}
                            <OptionValueInput optionId={option.id} />
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addOptionType}
                      disabled={productOptions.length >= 3} // Limiter à 3 options max
                      className={`mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${productOptions.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Ajouter une option (max 3)
                    </button>
                  </div>

                  {/* Section Variations générées */}
                  {productOptions.length > 0 && productOptions.every(opt => opt.name.trim() && opt.values.length > 0) && (
                    <div className="border-t pt-8">
                      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <h3 className="text-lg font-medium text-gray-900">
                          Variations du produit ({formData.variations.length})
                        </h3>
                        <span className="text-sm text-gray-500">
                          Générées automatiquement à partir des options
                        </span>
                      </div>

                      {formData.variations.length > 0 ? (
                        <div className="overflow-x-auto">
                          <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                              {/* Sous md : chaque ligne devient une carte ; dès md : tableau classique */}
                              <table className="block min-w-full divide-y divide-gray-300 md:table">
                                <thead className="hidden bg-gray-50 md:table-header-group">
                                  <tr>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Images
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Variation
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      SKU
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Prix ({getDeviseActive()}) *
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Prix barré
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Stock *
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Code-barres
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="block bg-white divide-y divide-gray-200 md:table-row-group">
                                  {formData.variations.map((variation, index) => {
                                    const variantImages = getVariantImages(variation.id);
                                    const productImages = getProductImages();
                                    const mainImage = productImages.find(img => img.is_main);
                                    const placeholderImageUrl = 'https://placehold.co/40x40/png?text=?';

                                    return (
                                      <tr key={variation.id} className="grid grid-cols-2 gap-3 p-4 hover:bg-gray-50 md:table-row md:p-0">
                                        <td className="min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          <div className="flex items-center space-x-2">
                                            <div className="flex -space-x-1">
                                              {variantImages.slice(0, 3).map((img, imgIndex) => (
                                                <div key={img.id} className="relative group">
                                                  <img
                                                    src={img.previewUrl || img.existingUrl || img.image_url}
                                                    alt={img.alt_text}
                                                    className="h-8 w-8 rounded border-2 border-white object-cover shadow-sm"
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={() => removeImage(img.id)}
                                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Supprimer"
                                                  >
                                                    ×
                                                  </button>
                                                </div>
                                              ))}
                                              {variantImages.length > 3 && (
                                                <div className="h-8 w-8 rounded border-2 border-white bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                                                  +{variantImages.length - 3}
                                                </div>
                                              )}
                                              {variantImages.length === 0 && (
                                                <img
                                                  src={mainImage?.previewUrl || mainImage?.existingUrl || mainImage?.image_url || placeholderImageUrl}
                                                  alt={`Variation ${variation.title}`}
                                                  className={`h-8 w-8 rounded object-cover ${!mainImage ? 'bg-gray-100' : ''}`}
                                                />
                                              )}
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => openImageSelectionModal(variation.id, variation.title)}
                                              className="cursor-pointer text-primary-600 hover:text-primary-800 flex-shrink-0 p-1 rounded hover:bg-primary-50"
                                              title="Associer des images par valeur d'option"
                                              disabled={isSubmitting}
                                            >
                                              <PhotoIcon className="h-4 w-4" />
                                            </button>
                                          </div>
                                        </td>
                                        <td className="min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          <div className="text-sm font-medium text-gray-900">
                                            {variation.title}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {Object.entries(variation.options || {}).map(([key, value]) => (
                                              <span key={key} className="inline-block mr-2">
                                                {key}: {value}
                                              </span>
                                            ))}
                                          </div>
                                        </td>
                                        <td className="col-span-2 min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          {/* Libellés visibles uniquement en mode carte (mobile) */}
                                          <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">SKU</span>
                                          <input
                                            type="text"
                                            value={variation.sku}
                                            readOnly
                                            className="w-full text-xs text-gray-500 bg-gray-100 border-gray-200 rounded px-2 py-1"
                                            title={variation.sku}
                                          />
                                        </td>
                                        <td className="min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">Prix ({getDeviseActive()}) *</span>
                                          <input
                                            type="text"
                                            value={variation.price}
                                            title={variation.price}
                                            onChange={(e) => handleVariationDetailChange(index, 'price', e.target.value)}
                                            placeholder="0"
                                            className={`w-full md:w-24 shadow-sm focus:ring-primary-500 focus:border-primary-500 block text-sm border-gray-300 rounded-md ${errors[`variation_${index}_price`] ? 'border-red-300' : ''}`}
                                          />
                                          {errors[`variation_${index}_price`] && <p className="mt-1 text-xs text-red-600">{errors[`variation_${index}_price`]}</p>}
                                        </td>
                                        <td className="min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">Prix barré</span>
                                          <input
                                            type="text"
                                            value={variation.compare_at_price}
                                            onChange={(e) => handleVariationDetailChange(index, 'compare_at_price', e.target.value)}
                                            placeholder="0"
                                            className="w-full md:w-24 shadow-sm focus:ring-primary-500 focus:border-primary-500 block text-sm border-gray-300 rounded-md"
                                          />
                                        </td>
                                        <td className="min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">Stock *</span>
                                          <input
                                            type="text"
                                            value={variation.stock}
                                            onChange={(e) => handleVariationDetailChange(index, 'stock', e.target.value)}
                                            placeholder="0"
                                            className={`w-full md:w-20 shadow-sm focus:ring-primary-500 focus:border-primary-500 block text-sm border-gray-300 rounded-md ${errors[`variation_${index}_stock`] ? 'border-red-300' : ''}`}
                                          />
                                          {errors[`variation_${index}_stock`] && <p className="mt-1 text-xs text-red-600">{errors[`variation_${index}_stock`]}</p>}
                                        </td>
                                        <td className="min-w-0 md:table-cell md:px-4 md:py-4 md:whitespace-nowrap">
                                          <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">Code-barres</span>
                                          <input
                                            type="text"
                                            value={variation.barcode}
                                            onChange={(e) => handleVariationDetailChange(index, 'barcode', e.target.value)}
                                            placeholder="Code-barres"
                                            className="w-full md:w-32 shadow-sm focus:ring-primary-500 focus:border-primary-500 block text-sm border-gray-300 rounded-md"
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                          <p className="text-sm">Aucune variation générée</p>
                          <p className="text-xs">Ajoutez des options et des valeurs ci-dessus</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </form>
      </div>

      {/* Boîte de dialogue pour l'insertion d'images via URL */}
      <ImageUrlDialog
        isOpen={imageUrlDialog.isOpen}
        onClose={closeImageUrlDialog}
        onInsert={handleInsertImageUrl}
      />

      {/* Modal NanoBanana */}
      <NanoBananaModal
        isOpen={isNanoBananaOpen}
        onClose={() => setIsNanoBananaOpen(false)}
        availableImages={formData.images}
        productContext={{
          name: formData.name,
          description: formData.description,
          category: formData.category,
          tags: formData.tags
        }}
        onGenerate={handleNanoBananaGenerate}
        isLoading={nanoBananaLoading}
      />

      {/* Modal de sélection d'images pour variants */}
      {imageSelectionModal.isOpen && imageSelectionModal.variant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 transition-opacity"
              onClick={closeImageSelectionModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Associer des images pour "{imageSelectionModal.variant.title}"
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Sélectionnez les valeurs d'options pour associer les images. Les images associées à une valeur d'option (ex: "Rouge") seront automatiquement utilisées pour toutes les variations de cette couleur.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeImageSelectionModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {formData.images.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <PhotoIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Aucune image disponible</p>
                    <p className="text-sm">Ajoutez d'abord des images au produit</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Sections par valeur d'option */}
                    {productOptions.map((option) => {
                      const variantOptionValue = imageSelectionModal.variant.options[option.name];

                      return (
                        <div key={option.name} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">
                              {option.name}: <span className="text-primary-600">{variantOptionValue}</span>
                            </h4>
                            <span className="text-sm text-gray-500">
                              {getImagesForOptionValue(option.name, variantOptionValue).length} image(s) -
                              {getVariantsForOptionValue(option.name, variantOptionValue).length} variation(s) concernée(s)
                            </span>
                          </div>

                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                            {formData.images.filter(img => !img.option_values || img.option_values[option.name] !== variantOptionValue).map((image) => {
                              const isAssociated = image.option_values && image.option_values[option.name] === variantOptionValue;

                              return (
                                <div
                                  key={image.id}
                                  className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${isAssociated
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  onClick={() => {
                                    if (isAssociated) {
                                      dissociateImageFromOptionValue(image.id, option.name);
                                    } else {
                                      associateImageToOptionValue(image.id, option.name, variantOptionValue);
                                    }
                                  }}
                                >
                                  <div className="aspect-square">
                                    <img
                                      src={image.previewUrl || image.existingUrl || image.image_url}
                                      alt={image.alt_text}
                                      className="h-full w-full object-cover"
                                    />
                                  </div>

                                  {/* Checkbox indicator */}
                                  <div className={`absolute top-1 right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isAssociated
                                    ? 'bg-primary-600 border-primary-600'
                                    : 'bg-white border-gray-300'
                                    }`}>
                                    {isAssociated && (
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>

                                  {/* Badge image principale */}
                                  {image.is_main && (
                                    <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                                      P
                                    </div>
                                  )}

                                  {/* Associations existantes */}
                                  {image.option_values && Object.keys(image.option_values).length > 0 && (
                                    <div className="absolute bottom-1 left-1 bg-gray-800 bg-opacity-75 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                                      {Object.values(image.option_values).join(', ')}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Images déjà associées à cette valeur d'option */}
                          {getImagesForOptionValue(option.name, variantOptionValue).length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-sm font-medium text-gray-700 mb-2">Images associées à toutes les variations "{variantOptionValue}" :</p>
                              <div className="flex flex-wrap gap-2">
                                {getImagesForOptionValue(option.name, variantOptionValue).map((image) => (
                                  <div key={image.id} className="relative group">
                                    <img
                                      src={image.previewUrl || image.existingUrl || image.image_url}
                                      alt={image.alt_text}
                                      className="h-12 w-12 rounded border-2 border-primary-500 object-cover"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => dissociateImageFromOptionValue(image.id, option.name)}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={closeImageSelectionModal}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Terminé
                </button>
                <p className="text-sm text-gray-500 self-center mr-4">
                  {getVariantImages(imageSelectionModal.variant.id).length} image(s) pour cette variation
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductFormPageComplete;
